import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { PrayerTimes } from "../../types/PrayerTypes";
import { PrayerState } from "../../hooks/usePrayerStateManager";
import { ScreenData } from "../../types/ScreenTypes";
import { Playlist, PlaylistItem } from "../../types/playlist.types";
import AnalogClock from "../elements/AnalogClock";
import BlankScreen from "../elements/BlankScreen";
import FitText from "../elements/FitText";
import CachedImage from "../shared/CachedImage";
import { masjidColourVars } from "../../utils/masjidColour";
import PrayerCountdown from "../elements/PrayerCountdown";
import NextPrayerBlock from "../elements/NextPrayerBlock";
import SimplePlaylistDisplayComponent from "../elements/SimplePlaylistDisplayComponent";
import { usePrayerStateManager } from "../../hooks/usePrayerStateManager";
import { HomePageGapCoordinatorProvider } from "../../hooks/useHomePageGapCoordinator";
import {
  getSunriseTimeDisplay,
  getJummahDisplayInfo,
  formatTimeDisplay,
  parseTimeToDate,
  formatCountdown,
  parseCountdownSegments,
} from "../../utils/timeUtils";
import {
  getNextPrayerTime,
  determineNextPrayer,
} from "../../utils/prayerTimeUtils";
import { PRAYER_TYPES } from "../../constants/prayerConstants";
import { runtimeConfig } from "../../config/runtime";
import "./shared-layout.css"; // Import shared CSS for flash animation
import { getCurrentTimeInTimezone } from "../../utils/timeUtils";
import {
  fetchEventsICS,
  hydrateEvents,
  HydratedEvent,
} from "../../services/eventsService";
import { ParsedEvent } from "../../db/eventsDb";
import { formatGregorianDate, formatHijriDate } from "../../utils/dateUtils";
import { getRotatedItems } from "../../utils/rotationUtils";
import { applyPrayerNameTokens, formatByTokenCase } from "../../utils/prayerNames";
import { HouseholdWordingContext, withHouseholdWording } from "../home/householdWording";
import { VerifiedBadge, VerifiedMasjidContext } from "../home/verifiedBadge";
import useLabbaikItems, { labbaikEvents } from "../../hooks/useLabbaikItems";

// Add this inside CustomDatabaseLayoutDisplay.tsx, after the imports
// rAF-based ticker that always enters from off-screen, with seamless looping
interface TickerElementProps {
  text: string;
  speed: number;
  fontSize: number;
  fontFamily: string;
  color: string;
  backgroundColor: string;
  direction: "left" | "right";
  pauseOnHover: boolean;
  containerWidth: number;
  containerHeight: number;
}

const TickerElement: React.FC<TickerElementProps> = ({
  text,
  speed,
  fontSize,
  fontFamily,
  color,
  backgroundColor,
  direction,
  pauseOnHover,
  containerWidth,
  containerHeight,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const posRef = useRef<number>(0);
  const isPausedRef = useRef(false);
  const [renderPos, setRenderPos] = useState(0);

  const duplicatedText = `${text}  •  ${text}`;

  useEffect(() => {
    if (!textRef.current) return;

    const singleTextWidth = textRef.current.offsetWidth / 2;

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    // Start off-screen: right edge for left-scroll, left edge for right-scroll
    posRef.current = direction === "left" ? containerWidth : -singleTextWidth;
    setRenderPos(posRef.current);

    const animate = () => {
      if (!isPausedRef.current) {
        if (direction === "left") {
          posRef.current -= speed / 60;
          if (posRef.current <= -singleTextWidth) {
            posRef.current = 0; // seamless loop
          }
        } else {
          posRef.current += speed / 60;
          if (posRef.current >= 0) {
            posRef.current = -singleTextWidth; // seamless loop
          }
        }
        setRenderPos(posRef.current);
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [text, speed, direction, containerWidth]);

  return (
    <div
      style={{
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
        backgroundColor,
        overflow: "hidden",
        position: "relative",
      }}
      onMouseEnter={
        pauseOnHover
          ? () => {
              isPausedRef.current = true;
            }
          : undefined
      }
      onMouseLeave={
        pauseOnHover
          ? () => {
              isPausedRef.current = false;
            }
          : undefined
      }
    >
      <div
        ref={textRef}
        style={{
          position: "absolute",
          left: `${renderPos}px`,
          top: "50%",
          transform: "translateY(-50%)",
          whiteSpace: "nowrap",
          fontSize: `${fontSize}px`,
          fontFamily,
          color,
          userSelect: "none",
        }}
      >
        {duplicatedText}
      </div>
    </div>
  );
};

// Helper function to show today's or tomorrow's special times (same as LandscapeLayout)
function getSpecialTimeDisplay(
  currentTimeStr: string,
  todayTime: string | undefined,
  tomorrowTime: string | undefined,
) {
  // If no value, return null
  if (!todayTime) return null;

  // If time has passed, show tomorrow's if available
  const now = currentTimeStr;
  const [nowH, nowM] = now.split(":").map(Number);
  const [todayH, todayM] = todayTime.split(":").map(Number);

  let isTomorrow = false;
  if (nowH > todayH || (nowH === todayH && nowM >= todayM)) {
    if (tomorrowTime) {
      isTomorrow = true;
    }
  }

  return {
    time: isTomorrow ? tomorrowTime : todayTime,
    isTomorrow,
  };
}

// Helper function to show Jamat times considering duration
function getJamatTimeDisplay(
  currentTimeStr: string,
  todayJamatTime: string | undefined,
  tomorrowJamatTime: string | undefined,
  duration: number | undefined, // Duration in seconds
) {
  // If no value, return null
  if (!todayJamatTime) return null;

  // Parse current time to seconds for precise comparison
  const [nowH, nowM] = currentTimeStr.split(":").map(Number);
  const nowInSeconds = nowH * 3600 + nowM * 60;

  // Parse today's Jamat time to seconds
  const [todayH, todayM] = todayJamatTime.split(":").map(Number);
  const todayJamatInSeconds = todayH * 3600 + todayM * 60;

  // Calculate end time (Jamat time + duration in seconds)
  const endTimeInSeconds = todayJamatInSeconds + (duration || 0);

  let isTomorrow = false;
  // If current time is at or after the Jamat time + duration, show tomorrow's if available
  if (nowInSeconds >= endTimeInSeconds) {
    if (tomorrowJamatTime) {
      isTomorrow = true;
    }
  }

  return {
    time: isTomorrow ? tomorrowJamatTime : todayJamatTime,
    isTomorrow,
  };
}

// Helper function to detect if a component is a playlist display element
function isPlaylistDisplayElement(component: any): boolean {
  const id = component.Id || component.id;

  // Check if this element has playlist display settings
  const playlistDisplay =
    component.playlistDisplay || component.PlaylistDisplay;

  // Either has playlist display settings or has a specific ID pattern
  return !!(playlistDisplay || (id && id.toLowerCase().includes("playlist")));
}

// Helper function to detect if a component is an image asset element
function isImageAssetElement(component: any): boolean {
  const id = component.Id || component.id;

  // Check if this element has image asset settings
  const imageAssetSettings =
    component.imageAssetSettings || component.ImageAssetSettings;

  // Either has image asset settings or has a specific ID pattern
  return !!(imageAssetSettings || (id && id.startsWith("imageAsset_")));
}

// Helper function to detect if a component is a flexible time element
function isFlexibleTimeElement(component: any): boolean {
  const id = component.Id || component.id;

  // Check if this element has flexible time settings
  const flexibleTimeSettings =
    component.flexibleTimeSettings || component.FlexibleTimeSettings;

  // Either has flexible time settings or has a specific ID pattern
  return !!(flexibleTimeSettings || (id && id.startsWith("flexibleTime_")));
}

// Helper function to detect if a component is a countdown display element
function isCountdownDisplayElement(component: any): boolean {
  const id = component.Id || component.id;

  // Check if this element has countdown display settings
  const countdownSettings =
    component.countdownDisplaySettings || component.CountdownDisplaySettings;

  // Either has countdown display settings or has a specific ID pattern
  return !!(countdownSettings || (id && id.startsWith("countdown_")));
}

interface CustomDatabaseLayoutDisplayProps {
  layoutDataJson: string; // Raw JSON from database LayoutData column
  prayerTimes: PrayerTimes;
  tomorrowPrayerTimes?: PrayerTimes | null; // Add tomorrow's prayer times
  masjidId: number;
  screenName?: string;
  screenData: ScreenData; // Add full screen data
  prayerState: PrayerState;
  // Previous Friday's Jummah times for detecting changes
  prevFridayJummah1?: string | null;
  prevFridayJummah2?: string | null;
  prevFridayJummah3?: string | null;
  prevFridayJummah4?: string | null;
  flashingPrayer?: string | null;
}

interface DatabaseLayoutData {
  // Root level properties (from API response)
  width?: number;
  height?: number;
  orientation?: "landscape" | "portrait";

  // Handle both capitalized and lowercase variants in Settings
  Settings?: {
    BackgroundColor?: string;
    backgroundColor?: string;
    SvgBackground?: {
      markup: string;
      colors: { [key: string]: string };
      originalColors: { [key: string]: string }; // Add this
    };
    svgBackground?: {
      markup: string;
      colors: { [key: string]: string };
      originalColors: { [key: string]: string }; // Add this
    };
    Orientation?: "landscape" | "portrait";
    orientation?: "landscape" | "portrait";
    Width?: number; // Capital W for screen width
    width?: number; // Keep lowercase as fallback
    Height?: number; // Capital H for screen height
    height?: number; // Keep lowercase as fallback
    masjidId?: number;
    screenName?: string;
    prayerPageDuration?: number;
    showChangesScreen?: boolean;
    changesScreenDuration?: number;
    enableEvents?: boolean;
    slideshowInterval?: number;
  };
  settings?: {
    BackgroundColor?: string;
    backgroundColor?: string;
    SvgBackground?: {
      markup: string;
      colors: { [key: string]: string };
      originalColors: { [key: string]: string }; // Add this
    };
    svgBackground?: {
      markup: string;
      colors: { [key: string]: string };
      originalColors: { [key: string]: string }; // Add this
    };
    orientation?: "landscape" | "portrait";
    width?: number;
    height?: number;
    masjidId?: number;
    screenName?: string;
    prayerPageDuration?: number;
    showChangesScreen?: boolean;
    changesScreenDuration?: number;
    enableEvents?: boolean;
  };
  Components?: Array<{
    Id?: string; // Capital I to match actual data
    id?: string; // Keep lowercase as fallback
    Visible?: boolean; // Support visibility from DB (capitalized)
    visible?: boolean; // Support visibility from DB (lowercase)
    Position?: { X?: number; Y?: number; x?: number; y?: number }; // Handle both cases
    position?: { x: number; y: number };
    Style?: {
      FontSize?: number;
      fontSize?: number;
      FontFamily?: string;
      fontFamily?: string;
      Color?: string;
      color?: string;
      FontWeight?: number;
      fontWeight?: number;
      IsBold?: boolean;
      isBold?: boolean;
      TextCase?: "none" | "uppercase" | "lowercase" | "capitalize";
      textCase?: "none" | "uppercase" | "lowercase" | "capitalize";
    };
    style?: {
      fontSize: number;
      fontFamily: string;
      color: string;
      fontWeight?: number;
      isBold?: boolean;
      textCase?: "none" | "uppercase" | "lowercase" | "capitalize";
    };
    Size?: {
      Width?: number;
      Height?: number;
      width?: number;
      height?: number;
    };
    size?: {
      width: number;
      height: number;
    };
    ClockStyle?: {
      ShowFace?: boolean;
      showFace?: boolean;
      ShowBorder?: boolean;
      showBorder?: boolean;
      ShowMarkings?: boolean;
      showMarkings?: boolean;
      ShowCenterPoint?: boolean;
      showCenterPoint?: boolean;
      HourHandColor?: string;
      hourHandColor?: string;
      MinuteHandColor?: string;
      minuteHandColor?: string;
      HourHandThickness?: number;
      hourHandThickness?: number;
      MinuteHandThickness?: number;
      minuteHandThickness?: number;
      HourHandLength?: number;
      hourHandLength?: number;
      MinuteHandLength?: number;
      minuteHandLength?: number;
      CenterPointSize?: number;
      centerPointSize?: number;
      CenterPointColor?: string;
      centerPointColor?: string;
      MinuteMarkerSize?: number;
      minuteMarkerSize?: number;
      MinuteMarkerThickness?: number;
      minuteMarkerThickness?: number;
      MinuteMarkerColor?: string;
      minuteMarkerColor?: string;
      ShowMinuteMarkers?: boolean;
      showMinuteMarkers?: boolean;
      Show5MinuteMarkers?: boolean;
      show5MinuteMarkers?: boolean;
      FiveMinuteMarkerSize?: number;
      fiveMinuteMarkerSize?: number;
      FiveMinuteMarkerThickness?: number;
      fiveMinuteMarkerThickness?: number;
      FiveMinuteMarkerColor?: string;
      fiveMinuteMarkerColor?: string;
      ClockBackgroundColor?: string;
      clockBackgroundColor?: string;
    };
    clockStyle?: {
      showFace: boolean;
      showBorder: boolean;
      showMarkings: boolean;
      showCenterPoint: boolean;
      hourHandColor: string;
      minuteHandColor: string;
      minuteMarkerSize?: number;
      minuteMarkerThickness?: number;
      minuteMarkerColor?: string;
      showMinuteMarkers?: boolean;
      show5MinuteMarkers?: boolean;
      fiveMinuteMarkerSize?: number;
      fiveMinuteMarkerThickness?: number;
      fiveMinuteMarkerColor?: string;
      clockBackgroundColor?: string;
    };
    CustomText?: string; // Add custom text for Makrooh elements (capitalized)
    customText?: string; // Add custom text for Makrooh elements (lowercase)
    ShowCountdownTimer?: boolean; // Makrooh countdown visibility toggle (capitalized)
    showCountdownTimer?: boolean; // Makrooh countdown visibility toggle (lowercase)
    CountdownLabelText?: string; // Makrooh countdown label text (capitalized)
    countdownLabelText?: string; // Makrooh countdown label text (lowercase)
    CountdownOnNewLine?: boolean; // Makrooh countdown on new line (capitalized)
    countdownOnNewLine?: boolean; // Makrooh countdown on new line (lowercase)
    ConditionalOverlay?: {
      DaysActive?: number[];
      daysActive?: number[];
      TimeRangeStart?: {
        Day?: number;
        day?: number;
        Hour?: number;
        hour?: number;
        Minute?: number;
        minute?: number;
      };
      timeRangeStart?: {
        day: number;
        hour: number;
        minute: number;
      };
      TimeRangeEnd?: {
        Day?: number;
        day?: number;
        Hour?: number;
        hour?: number;
        Minute?: number;
        minute?: number;
      };
      timeRangeEnd?: {
        day: number;
        hour: number;
        minute: number;
      };
      PrayerRangeStart?: {
        Day?: number;
        day?: number;
        Prayer?: string;
        prayer?: string;
        Offset?: number;
        offset?: number;
      };
      prayerRangeStart?: {
        day: number;
        prayer: string;
        offset: number;
      };
      PrayerRangeEnd?: {
        Day?: number;
        day?: number;
        Prayer?: string;
        prayer?: string;
        Offset?: number;
        offset?: number;
      };
      prayerRangeEnd?: {
        day: number;
        prayer: string;
        offset: number;
      };
      DefaultText?: string;
      defaultText?: string;
      ActiveText?: string;
      activeText?: string;
      BackgroundColor?: string;
      backgroundColor?: string;
      Width?: number;
      width?: number;
      Height?: number;
      height?: number;
      FontSize?: number;
      fontSize?: number;
      FontFamily?: string;
      fontFamily?: string;
      FontColor?: string;
      fontColor?: string;
      IsBold?: boolean;
      isBold?: boolean;
      IsItalic?: boolean;
      isItalic?: boolean;
      IsUnderline?: boolean;
      isUnderline?: boolean;
      TextAlign?: string;
      textAlign?: string;
    };
    conditionalOverlay?: {
      daysActive: number[];
      timeRangeStart?: {
        day: number;
        hour: number;
        minute: number;
      };
      timeRangeEnd?: {
        day: number;
        hour: number;
        minute: number;
      };
      prayerRangeStart?: {
        day: number;
        prayer: string;
        offset: number;
      };
      prayerRangeEnd?: {
        day: number;
        prayer: string;
        offset: number;
      };
      defaultText: string;
      activeText: string;
      backgroundColor?: string;
      width?: number;
      height?: number;
      fontSize?: number;
      fontFamily?: string;
      fontColor?: string;
      isBold?: boolean;
      isItalic?: boolean;
      isUnderline?: boolean;
      textAlign?: string;
    };
    // Add ticker settings support
    TickerSettings?: {
      Text?: string;
      text?: string;
      Mode?: "static" | "dynamic";
      mode?: "static" | "dynamic";
      Speed?: number;
      speed?: number;
      FontSize?: number;
      fontSize?: number;
      FontFamily?: string;
      fontFamily?: string;
      Color?: string;
      color?: string;
      BackgroundColor?: string;
      backgroundColor?: string;
      Direction?: "left" | "right";
      direction?: "left" | "right";
      PauseOnHover?: boolean;
      pauseOnHover?: boolean;
    };
    tickerSettings?: {
      text: string;
      mode: "static" | "dynamic";
      speed: number;
      fontSize: number;
      fontFamily: string;
      color: string;
      backgroundColor?: string;
      direction: "left" | "right";
      pauseOnHover?: boolean;
    };
    // Add flexible time settings for custom prayer time displays
    FlexibleTimeSettings?: {
      Mode?: "mirror" | "offset";
      mode?: "mirror" | "offset";
      SourceField?: string;
      sourceField?: string;
      OffsetMinutes?: number;
      offsetMinutes?: number;
      Label?: string;
      label?: string;
      ShowTomorrowAfterPassed?: boolean;
      showTomorrowAfterPassed?: boolean;
    };
    flexibleTimeSettings?: {
      mode: "mirror" | "offset";
      sourceField: string;
      offsetMinutes: number;
      label?: string;
      showTomorrowAfterPassed: boolean;
    };
    // Countdown display settings for standalone countdown elements
    CountdownDisplaySettings?: {
      CountdownTarget?: string;
      countdownTarget?: string;
      HideWhenInactive?: boolean;
      hideWhenInactive?: boolean;
      CountdownFormat?: "hh:mm:ss" | "hh:mm" | "mm:ss";
      countdownFormat?: "hh:mm:ss" | "hh:mm" | "mm:ss";
      DuringSalahBehavior?: "none" | "salahRemaining" | "pauseDuringSalah";
      duringSalahBehavior?: "none" | "salahRemaining" | "pauseDuringSalah";
      PreSalahLabel?: string;
      preSalahLabel?: string;
      DuringSalahLabel?: string;
      duringSalahLabel?: string;
      LabelStyle?: {
        FontSize?: number;
        fontSize?: number;
        FontFamily?: string;
        fontFamily?: string;
        Color?: string;
        color?: string;
        FontWeight?: number;
        fontWeight?: number;
        IsBold?: boolean;
        isBold?: boolean;
        TextAlign?: "left" | "center" | "right";
        textAlign?: "left" | "center" | "right";
      };
      labelStyle?: {
        fontSize?: number;
        fontFamily?: string;
        color?: string;
        fontWeight?: number;
        isBold?: boolean;
        textAlign?: "left" | "center" | "right";
      };
    };
    countdownDisplaySettings?: {
      countdownTarget?: string;
      hideWhenInactive?: boolean;
      countdownFormat?: "hh:mm:ss" | "hh:mm" | "mm:ss";
      duringSalahBehavior?: "none" | "salahRemaining" | "pauseDuringSalah";
      preSalahLabel?: string;
      duringSalahLabel?: string;
      labelStyle?: {
        fontSize?: number;
        fontFamily?: string;
        color?: string;
        fontWeight?: number;
        isBold?: boolean;
        textAlign?: "left" | "center" | "right";
      };
    };
    // Next prayer highlight settings
    NextPrayerHighlight?: {
      Enabled?: boolean;
      enabled?: boolean;
      Color?: string;
      color?: string;
      BackgroundColor?: string;
      backgroundColor?: string;
      GlowColor?: string;
      glowColor?: string;
      Scale?: number;
      scale?: number;
    };
    nextPrayerHighlight?: {
      enabled: boolean;
      color?: string;
      backgroundColor?: string;
      glowColor?: string;
      scale?: number;
    };
    // Adhan display settings
    AdhanDisplaySettings?: {
      DisplayMode?: string;
      displayMode?: string;
    };
    adhanDisplaySettings?: {
      displayMode: string;
    };
  }>;
  components?: Array<{
    id: string;
    visible?: boolean;
    position: { x: number; y: number };
    style?: {
      fontSize: number;
      fontFamily: string;
      color: string;
      textCase?: "none" | "uppercase" | "lowercase" | "capitalize";
    };
    size?: {
      width: number;
      height: number;
    };
    clockStyle?: {
      showFace: boolean;
      showBorder: boolean;
      showMarkings: boolean;
      showCenterPoint: boolean;
      hourHandColor: string;
      minuteHandColor: string;
      hourHandThickness?: number;
      minuteHandThickness?: number;
      hourHandLength?: number;
      minuteHandLength?: number;
      centerPointSize?: number;
      centerPointColor?: string;
      minuteMarkerSize?: number;
      minuteMarkerThickness?: number;
      minuteMarkerColor?: string;
      showMinuteMarkers?: boolean;
      show5MinuteMarkers?: boolean;
      fiveMinuteMarkerSize?: number;
      fiveMinuteMarkerThickness?: number;
      fiveMinuteMarkerColor?: string;
      clockBackgroundColor?: string;
    };
    customText?: string; // Add custom text for Makrooh elements
    showCountdownTimer?: boolean; // Makrooh countdown visibility toggle
    countdownLabelText?: string; // Makrooh countdown label text
    countdownOnNewLine?: boolean; // Makrooh countdown on new line
    flexibleTimeSettings?: {
      mode: "mirror" | "offset";
      sourceField: string;
      offsetMinutes: number;
      label?: string;
      showTomorrowAfterPassed: boolean;
    };
    countdownDisplaySettings?: {
      countdownTarget?: string;
      hideWhenInactive?: boolean;
      countdownFormat?: "hh:mm:ss" | "hh:mm" | "mm:ss";
      duringSalahBehavior?: "none" | "salahRemaining" | "pauseDuringSalah";
      preSalahLabel?: string;
      duringSalahLabel?: string;
      labelStyle?: {
        fontSize?: number;
        fontFamily?: string;
        color?: string;
        fontWeight?: number;
        isBold?: boolean;
        textAlign?: "left" | "center" | "right";
      };
    };
    nextPrayerHighlight?: {
      enabled: boolean;
      color?: string;
      backgroundColor?: string;
      glowColor?: string;
      scale?: number;
    };
    adhanDisplaySettings?: {
      displayMode: string;
    };
  }>;
  slideshowImages?: Array<{
    url: string;
    duration: number;
    objectFit: "cover" | "contain" | "fill";
  }>;
}

export default function CustomDatabaseLayoutDisplay({
  layoutDataJson,
  prayerTimes,
  tomorrowPrayerTimes = null, // Default to null
  masjidId,
  screenName,
  screenData: incomingScreenData,
  prayerState, // Include in destructuring
  prevFridayJummah1 = null,
  prevFridayJummah2 = null,
  prevFridayJummah3 = null,
  prevFridayJummah4 = null,
  flashingPrayer = null,
}: CustomDatabaseLayoutDisplayProps) {
  // On a household tablet its own word and spellings win over the masjid's; wall screens have none.
  const wording = useContext(HouseholdWordingContext);
  // Household tablets badge a masjid with official salah times; wall screens never get this.
  const verified = useContext(VerifiedMasjidContext);
  const screenData = useMemo(
    () => withHouseholdWording(incomingScreenData, wording),
    [incomingScreenData, wording],
  );
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentTimeStr, setCurrentTimeStr] = useState<string>("");
  const [currentDateStr, setCurrentDateStr] = useState<string>(() => {
    // Initialize with current date string in the configured timezone
    const now = getCurrentTimeInTimezone(screenData.timeZone);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  });
  const [nextPrayer, setNextPrayer] = useState<number>(0);
  const [layoutData, setLayoutData] = useState<DatabaseLayoutData | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [screenDimensions, setScreenDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [events, setEvents] = useState<HydratedEvent[]>([]);

  // Process screenData to make all blob URLs absolute
  const processedScreenData = React.useMemo(() => {
    const processed = { ...screenData };
    if (processed.backgroundUrl) {
      processed.backgroundUrl = runtimeConfig.getBlobUrl(
        processed.backgroundUrl,
      );
    }
    if (processed.playlists) {
      processed.playlists = processed.playlists.map((playlist) => ({
        ...playlist,
        items:
          playlist.items?.map((item) => ({
            ...item,
            assetUrl: runtimeConfig.getBlobUrl((item as any).assetUrl),
          })) || [],
      }));
    }
    return processed;
  }, [screenData]);

  // Use our prayer state manager hook
  const {
    isCountdownActive,
    countdownPrayer,
    countdownSeconds,
    isMakroohTime,
    makroohReason,
    makroohSecondsLeft,
    isScreenBlank,
    currentIslamicDate,
  } = usePrayerStateManager(screenData, prayerTimes, tomorrowPrayerTimes);

  // Format Islamic date (same as LandscapeLayout)
  const formatIslamicDate = (dateValue: any): string => {
    if (typeof dateValue === "string") {
      return dateValue;
    }

    if (dateValue && typeof dateValue === "object") {
      if (dateValue.FormattedDate) {
        return dateValue.FormattedDate;
      }

      if (dateValue.Day && dateValue.MonthName && dateValue.Year) {
        return `${dateValue.Day} ${dateValue.MonthName} ${dateValue.Year}`;
      }
    }

    return "--";
  };

  const formattedIslamicDate = React.useMemo(() => {
    const dateValue = prayerState.currentIslamicDate;
    if (typeof dateValue === "string") return dateValue;
    if (dateValue && typeof dateValue === "object" && dateValue !== null) {
      const day = (dateValue as any).Day || "";
      const monthName = (dateValue as any).MonthName || "";
      const year = (dateValue as any).Year || "";
      const format = screenData.hijriDateFormat || "dd MMMM yyyy";
      return formatHijriDate(
        day,
        monthName,
        year,
        format,
        screenData.customizedNames,
      );
    }
    return "--";
  }, [
    prayerState.currentIslamicDate,
    screenData.hijriDateFormat,
    screenData.customizedNames,
  ]);

  // Format current date - depends on currentDateStr state which is reliably updated at midnight
  const formattedGregorianDate = React.useMemo(() => {
    const today = getCurrentTimeInTimezone(screenData.timeZone);
    const format = screenData.gregorianDateFormat || "dddd, MMMM dd, yyyy";
    return formatGregorianDate(today, format);
  }, [screenData.gregorianDateFormat, currentDateStr, screenData.timeZone]);

  // Enhanced time formatting with period styling (same as LandscapeLayout)
  const formatTimeWithPeriod = (
    timeStr: string | null | undefined,
    use12Hour: boolean = true,
    showPeriod: boolean = true, // New parameter to control AM/PM display
  ) => {
    if (!timeStr || timeStr === "--:--") return "--:--";

    try {
      const formatted = formatTimeDisplay(timeStr, use12Hour);

      // If we don't want to show period or not using 12-hour format, return without period
      if (
        !showPeriod ||
        !use12Hour ||
        (!formatted.includes("am") && !formatted.includes("pm"))
      ) {
        // Remove any existing am/pm from the formatted time
        return formatted.replace(/\s*(am|pm)$/i, "");
      }

      // Find the position of "am" or "pm"
      const amIndex = formatted.indexOf("am");
      const pmIndex = formatted.indexOf("pm");
      const periodIndex = amIndex >= 0 ? amIndex : pmIndex;

      if (periodIndex >= 0) {
        const timePart = formatted.substring(0, periodIndex);
        const periodPart = formatted.substring(periodIndex, periodIndex + 2);

        return (
          <>
            {timePart}
            <span className="period" style={{ fontSize: "60%" }}>
              {periodPart}
            </span>
          </>
        );
      }

      return formatted;
    } catch {
      return "--:--";
    }
  };

  // Get design resolution from layout data or fall back to defaults
  const getDesignDimensions = () => {
    const settingsCapital = layoutData?.Settings;
    const settingsLower = layoutData?.settings;

    // Try to get width/height from multiple possible locations:
    // 1. Root level (direct from API response)
    // 2. Settings object (capitalized)
    // 3. settings object (lowercase)
    const rawWidth =
      layoutData?.width || // Root level first
      settingsCapital?.Width ||
      settingsLower?.width ||
      settingsCapital?.width ||
      1920;
    const rawHeight =
      layoutData?.height || // Root level first
      settingsCapital?.Height ||
      settingsLower?.height ||
      settingsCapital?.height ||
      1080;
    const orientation =
      layoutData?.orientation || // Root level first
      settingsCapital?.Orientation ||
      settingsLower?.orientation ||
      settingsCapital?.orientation ||
      "landscape";

    // For portrait orientation, swap width and height if needed
    // The API might send 1920x1080 but for portrait we want 1080x1920
    let width = rawWidth;
    let height = rawHeight;

    if (orientation === "portrait") {
      // If we got landscape dimensions for portrait mode, swap them
      if (rawWidth > rawHeight) {
        width = rawHeight;
        height = rawWidth;
      }
    }

    return { width, height, orientation };
  };

  const designDimensions = layoutData
    ? getDesignDimensions()
    : { width: 1920, height: 1080, orientation: "landscape" };
  const DESIGN_WIDTH = designDimensions.width;
  const DESIGN_HEIGHT = designDimensions.height;

  // Compute a uniform scale so the design surface fits the screen
  const scale = Math.min(
    screenDimensions.width / DESIGN_WIDTH,
    screenDimensions.height / DESIGN_HEIGHT,
  );
  const scaledWidth = DESIGN_WIDTH * scale;
  const scaledHeight = DESIGN_HEIGHT * scale;
  const offsetLeft = (screenDimensions.width - scaledWidth) / 2;
  const offsetTop = (screenDimensions.height - scaledHeight) / 2;

  // Update screen dimensions on window resize
  useEffect(() => {
    const handleResize = () => {
      setScreenDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update time every second and calculate next prayer
  useEffect(() => {
    function updateTime() {
      const now = getCurrentTimeInTimezone(screenData.timeZone);

      setCurrentTime(now);

      const hours = now.getHours();
      const minutes = now.getMinutes();
      const timeStr = `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
      setCurrentTimeStr(timeStr);

      // Update date string - triggers Gregorian date recalculation at midnight
      const newDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      setCurrentDateStr((prev) => {
        if (prev !== newDateStr) {
          console.log(
            `📅 [CustomDatabaseLayout] Date changed: ${prev} → ${newDateStr}`,
          );
          return newDateStr;
        }
        return prev;
      });

      const next = determineNextPrayer(
        prayerTimes,
        tomorrowPrayerTimes,
        timeStr,
        screenData,
      );
      setNextPrayer(next);
    }

    updateTime();
    const timer = setInterval(updateTime, 1000);

    return () => clearInterval(timer);
  }, [prayerTimes, tomorrowPrayerTimes, screenData]);

  // Bulletproof date update: Handle page visibility changes (browser tab wake-up, screen on)
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        // Page became visible - check if date has changed while hidden
        const now = getCurrentTimeInTimezone(screenData.timeZone);
        const newDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
        setCurrentDateStr((prev) => {
          if (prev !== newDateStr) {
            console.log(
              `📅 [CustomDatabaseLayout] Date updated on visibility: ${prev} → ${newDateStr}`,
            );
            return newDateStr;
          }
          return prev;
        });
        setCurrentTime(now);
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [screenData.timeZone]);

  // Bulletproof date update: Periodic sanity check every 60 seconds (catches edge cases)
  useEffect(() => {
    const sanityCheck = setInterval(() => {
      const now = getCurrentTimeInTimezone(screenData.timeZone);
      const newDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      setCurrentDateStr((prev) => {
        if (prev !== newDateStr) {
          console.log(
            `📅 [CustomDatabaseLayout] Date corrected by sanity check: ${prev} → ${newDateStr}`,
          );
          return newDateStr;
        }
        return prev;
      });
    }, 60000); // Every 60 seconds

    return () => clearInterval(sanityCheck);
  }, [screenData.timeZone]);

  // Fetch events from ICS calendar (only when events are enabled on the screen)
  useEffect(() => {
    if (!screenData.showUpcomingEvents) return;

    const loadEvents = async () => {
      try {
        const parsedEvents = await fetchEventsICS(
          masjidId,
          screenData.timesRefreshInterval,
        );
        const hydratedEvents = hydrateEvents(parsedEvents);
        setEvents(hydratedEvents);
      } catch (error) {
        console.error("Failed to fetch events for ticker:", error);
        setEvents([]);
      }
    };

    const handleEventsUpdated = () => {
      loadEvents();
    };

    loadEvents(); // Initial load

    window.addEventListener("events-updated", handleEventsUpdated);
    return () =>
      window.removeEventListener("events-updated", handleEventsUpdated);
  }, [
    masjidId,
    screenData.timesRefreshInterval,
    screenData.showUpcomingEvents,
  ]);

  const labbaikItems = useLabbaikItems(
    masjidId,
    screenData.showLabbaik && screenData.showUpcomingEvents,
  );
  const tickerEvents = React.useMemo(
    () => [...events, ...labbaikEvents(labbaikItems, new Date())],
    [events, labbaikItems, currentDateStr],
  );

  // Parse layout data from JSON
  useEffect(() => {
    try {
      if (!layoutDataJson) {
        setLayoutData(null);
        return;
      }

      const parsed = JSON.parse(layoutDataJson);
      setLayoutData(parsed);
      setParseError(null);

      // Debug: Log each component's visibility (removed for production)
    } catch (error) {
      console.error("Failed to parse layout data JSON:", error);
      setParseError(error instanceof Error ? error.message : "Parse error");
      setLayoutData(null);
    }
  }, [layoutDataJson]);

  // If screen should be blank, show the blank screen (same as LandscapeLayout)
  if (isScreenBlank) {
    return (
      <BlankScreen
        message={screenData.salahInProgressMessage}
        hijriDate={formattedIslamicDate}
        gregorianDate={formattedGregorianDate}
        screenData={screenData}
        showDateTime={true}
        backgroundUrl={screenData.blankScreenBackgroundUrl}
        countdownSecondsLeft={prayerState.blankScreenSecondsLeft}
        countdownTotalSeconds={prayerState.blankScreenTotalSeconds}
        countdownPrayer={prayerState.blankScreenPrayer}
      />
    );
  }

  // Helper function to check if conditional overlay is active
  const isConditionalOverlayActive = (overlaySettings: any): boolean => {
    if (!overlaySettings) {
      return false;
    }

    const now = getCurrentTimeInTimezone(screenData.timeZone);
    // The Home templates name the Dhuhr slot "{Dhuhr}", or "{Jummah}" on a Friday. Once its jama'ah
    // has passed the slot shows tomorrow's prayer, so that name follows the slot's day, not today's.
    const namesDhuhrSlot =
      /\{dhuhr\}/i.test(overlaySettings.DefaultText || overlaySettings.defaultText || "") &&
      /\{jummah\}/i.test(overlaySettings.ActiveText || overlaySettings.activeText || "");
    const currentDay =
      namesDhuhrSlot && isShowingTomorrow("dhuhr") ? (now.getDay() + 1) % 7 : now.getDay(); // 0 = Sunday
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // Extract days active (support both capitalized and lowercase)
    const daysActive =
      overlaySettings.DaysActive || overlaySettings.daysActive || [];

    // Check if current day is in active days
    if (!daysActive.includes(currentDay)) {
      return false;
    }

    // Check for prayer-based time ranges first (new format)
    const prayerStart =
      overlaySettings.PrayerRangeStart || overlaySettings.prayerRangeStart;
    const prayerEnd =
      overlaySettings.PrayerRangeEnd || overlaySettings.prayerRangeEnd;

    if (prayerStart || prayerEnd) {
      // Prayer-based time range logic
      // For multi-day ranges, we need to check if current day/time falls within the range

      const startDay = prayerStart?.Day ?? prayerStart?.day;
      const endDay = prayerEnd?.Day ?? prayerEnd?.day;
      const startPrayer = prayerStart?.Prayer ?? prayerStart?.prayer;
      const endPrayer = prayerEnd?.Prayer ?? prayerEnd?.prayer;
      const startOffset = prayerStart?.Offset ?? prayerStart?.offset ?? 0;
      const endOffset = prayerEnd?.Offset ?? prayerEnd?.offset ?? 0;

      // If no valid prayer range config, just check if day is active
      if (!startPrayer && !endPrayer) {
        return true; // Day is active, no time restriction
      }

      // Get prayer times for calculations
      const startPrayerTime = startPrayer
        ? (prayerTimes as any)[startPrayer]
        : null;
      const endPrayerTime = endPrayer ? (prayerTimes as any)[endPrayer] : null;

      // Helper to parse time string to minutes since midnight
      const timeToMinutes = (timeStr: string): number => {
        const [h, m] = timeStr.split(":").map(Number);
        return h * 60 + m;
      };

      const currentMinutes = currentHour * 60 + currentMinute;

      // Case 1: Same day range (e.g., Friday 12:00 to Friday 14:00)
      if (startDay === endDay && startDay === currentDay) {
        const startMinutes = startPrayerTime
          ? timeToMinutes(startPrayerTime) + startOffset
          : 0;
        const endMinutes = endPrayerTime
          ? timeToMinutes(endPrayerTime) + endOffset
          : 24 * 60;
        return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
      }

      // Case 2: Multi-day range - check if we're in the active window
      if (
        startDay !== undefined &&
        endDay !== undefined &&
        startDay !== endDay
      ) {
        // Check if current day is the start day - must be AFTER start time
        if (currentDay === startDay && startPrayerTime) {
          const startMinutes = timeToMinutes(startPrayerTime) + startOffset;
          return currentMinutes >= startMinutes;
        }

        // Check if current day is the end day - must be BEFORE end time
        if (currentDay === endDay && endPrayerTime) {
          const endMinutes = timeToMinutes(endPrayerTime) + endOffset;
          return currentMinutes <= endMinutes;
        }

        // Check if current day is between start and end days
        // Handle week wraparound (e.g., Friday to Monday)
        if (startDay < endDay) {
          // Simple case: start < end (e.g., Thursday to Friday)
          if (currentDay > startDay && currentDay < endDay) {
            return true;
          }
        } else {
          // Week wraparound case (e.g., Friday to Monday)
          if (currentDay > startDay || currentDay < endDay) {
            return true;
          }
        }
      }

      // Case 3: Only start day specified - active from that prayer time onwards on that day
      if (startDay !== undefined && endDay === undefined) {
        if (currentDay === startDay && startPrayerTime) {
          const startMinutes = timeToMinutes(startPrayerTime) + startOffset;
          return currentMinutes >= startMinutes;
        }
      }

      // Case 4: Only end day specified - active until that prayer time on that day
      if (startDay === undefined && endDay !== undefined) {
        if (currentDay === endDay && endPrayerTime) {
          const endMinutes = timeToMinutes(endPrayerTime) + endOffset;
          return currentMinutes <= endMinutes;
        }
      }

      // If we get here, the prayer range doesn't match the current day/time
      // Return false since we have a specific prayer range configured but we're outside it
      return false;
    }

    // Fallback to legacy time range format for backward compatibility
    const timeStart =
      overlaySettings.TimeRangeStart || overlaySettings.timeRangeStart;
    const timeEnd =
      overlaySettings.TimeRangeEnd || overlaySettings.timeRangeEnd;

    if (!timeStart || !timeEnd) {
      return true; // Active for entire day if no time range
    }

    // Extract time range values
    const startDay = timeStart.Day ?? timeStart.day ?? currentDay;
    const startHour = timeStart.Hour ?? timeStart.hour ?? 0;
    const startMinute = timeStart.Minute ?? timeStart.minute ?? 0;

    const endDay = timeEnd.Day ?? timeEnd.day ?? currentDay;
    const endHour = timeEnd.Hour ?? timeEnd.hour ?? 23;
    const endMinute = timeEnd.Minute ?? timeEnd.minute ?? 59;

    // Convert to minutes for easier comparison
    const currentTotalMinutes =
      currentDay * 24 * 60 + currentHour * 60 + currentMinute;
    const startTotalMinutes = startDay * 24 * 60 + startHour * 60 + startMinute;
    const endTotalMinutes = endDay * 24 * 60 + endHour * 60 + endMinute;

    const isActive =
      currentTotalMinutes >= startTotalMinutes &&
      currentTotalMinutes <= endTotalMinutes;
    return isActive;
  }; // Helper function to format seconds as mm:ss
  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const formatStandaloneCountdown = (
    totalSeconds: number,
    format: "hh:mm:ss" | "hh:mm" | "mm:ss",
  ): string => {
    const safeSeconds = Math.max(0, totalSeconds);
    const segments = parseCountdownSegments(safeSeconds);

    if (format === "hh:mm") {
      return `${segments.hours}:${segments.minutes}`;
    }

    if (format === "mm:ss") {
      const totalMinutes = Math.floor(safeSeconds / 60)
        .toString()
        .padStart(2, "0");
      return `${totalMinutes}:${segments.seconds}`;
    }

    return `${segments.hours}:${segments.minutes}:${segments.seconds}`;
  };

  // Helper function to subtract minutes from a "HH:MM" time string
  const subtractMinutesFromTime = (
    timeStr: string,
    minutes: number,
  ): string => {
    if (!timeStr || timeStr === "--:--") return "--:--";
    const parts = timeStr.split(":");
    if (parts.length < 2) return "--:--";
    const hours = parseInt(parts[0], 10);
    const mins = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(mins)) return "--:--";
    const totalMinutes = hours * 60 + mins - minutes;
    const adjusted = ((totalMinutes % 1440) + 1440) % 1440; // wrap around midnight
    const h = Math.floor(adjusted / 60);
    const m = adjusted % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  };

  // Helper: Find the next upcoming prayer (name, Date, and raw jamaah time string)
  // Falls back to tomorrow's Fajr when all today's prayers have passed.
  const findNextPrayer = (
    pt: PrayerTimes,
    sd: ScreenData,
    countdownTarget: string,
    tomorrowPt?: PrayerTimes | null,
  ): { name: string; time: Date; jamaahTime: string } | null => {
    const now = getCurrentTimeInTimezone(sd.timeZone);
    const nowMs = now.getTime();
    const isAdhan = countdownTarget === "adhan";
    const offsets: Record<string, number> = {
      fajr: sd.fajrAdhanOffset || 0,
      dhuhr: sd.dhuhrAdhanOffset || 0,
      asar: sd.asarAdhanOffset || 0,
      maghrib: sd.maghribAdhanOffset || 0,
      isha: sd.ishaAdhanOffset || 0,
      jummah: sd.jummahAdhanOffset || 0,
      jummahLater: sd.jummahAdhanOffset || 0,
    };

    const getPrayerDurationSeconds = (key: string): number => {
      if (key === "fajr") return Math.max(0, sd.fajrLength || 0);
      if (key === "dhuhr") return Math.max(0, sd.dhuhrLength || 0);
      if (key === "asar") return Math.max(0, sd.asarLength || 0);
      if (key === "maghrib") return Math.max(0, sd.maghribLength || 0);
      if (key === "isha") return Math.max(0, sd.ishaLength || 0);
      if (key === "jummah") return Math.max(0, sd.jummahLength || 0);
      // Jumu'ah 2-4 last 15 minutes, as in getPrayerLength and getJummahDisplayInfo
      if (key === "jummahLater") return 15 * 60;
      return 0;
    };

    const baseJummahName = sd.customizedNames?.jummah || "Jummah";
    const fridayJummahCandidates = [
      {
        key: "jummah",
        name: baseJummahName,
        time: pt.Jummah1,
      },
      {
        key: "jummahLater",
        name: `${baseJummahName} 2`,
        time: pt.Jummah2,
      },
      {
        key: "jummahLater",
        name: `${baseJummahName} 3`,
        time: pt.Jummah3,
      },
      {
        key: "jummahLater",
        name: `${baseJummahName} 4`,
        time: pt.Jummah4,
      },
    ].filter((candidate) => candidate.time && candidate.time !== "--:--");

    const list = [
      {
        key: "fajr",
        name: sd.customizedNames?.fajr || "Fajr",
        time: pt.JamFajr,
      },
      ...(now.getDay() === 5 && fridayJummahCandidates.length > 0
        ? fridayJummahCandidates
        : [
            {
              key: "dhuhr",
              name: sd.customizedNames?.dhuhr || "Dhuhr",
              time: pt.JamDhuhr,
            },
          ]),
      {
        key: "asar",
        name: sd.customizedNames?.asar || "Asar",
        time: pt.JamAsar,
      },
      {
        key: "maghrib",
        name: sd.customizedNames?.maghrib || "Maghrib",
        time: pt.JamMaghrib,
      },
      {
        key: "isha",
        name: sd.customizedNames?.isha || "Isha",
        time: pt.JamEsha,
      },
    ];

    const active: {
      name: string;
      time: Date;
      jamaahTime: string;
      jamaahDate: Date;
    }[] = [];
    const upcoming: {
      name: string;
      time: Date;
      jamaahTime: string;
      jamaahDate: Date;
    }[] = [];

    for (const p of list) {
      if (!p.time || p.time === "--:--") continue;

      const jamaahDate = parseTimeToDate(p.time);
      if (!jamaahDate) continue;

      const prayerEndMs =
        jamaahDate.getTime() + getPrayerDurationSeconds(p.key) * 1000;
      if (nowMs >= prayerEndMs) continue;

      const target = isAdhan
        ? subtractMinutesFromTime(p.time, offsets[p.key] || 0)
        : p.time;

      const targetDate = parseTimeToDate(target);
      if (!targetDate) continue;

      const candidate = {
        name: p.name,
        time: targetDate,
        jamaahTime: p.time,
        jamaahDate,
      };

      if (jamaahDate.getTime() <= nowMs) {
        active.push(candidate);
      } else {
        upcoming.push(candidate);
      }
    }

    // While a prayer is in its active window, keep showing that prayer.
    active.sort((a, b) => b.jamaahDate.getTime() - a.jamaahDate.getTime());
    if (active.length > 0) {
      const { name, time, jamaahTime } = active[0];
      return { name, time, jamaahTime };
    }

    upcoming.sort((a, b) => a.time.getTime() - b.time.getTime());

    if (upcoming.length > 0) {
      const { name, time, jamaahTime } = upcoming[0];
      return { name, time, jamaahTime };
    }

    // All today's prayers have passed — roll over to tomorrow's Fajr
    const tomorrowFajrTime = tomorrowPt?.JamFajr;
    if (tomorrowFajrTime && tomorrowFajrTime !== "--:--") {
      const target = isAdhan
        ? subtractMinutesFromTime(tomorrowFajrTime, offsets.fajr || 0)
        : tomorrowFajrTime;
      const d = parseTimeToDate(target);
      if (d) {
        // Shift to tomorrow
        d.setDate(d.getDate() + 1);
        return {
          name: sd.customizedNames?.fajr || "Fajr",
          time: d,
          jamaahTime: tomorrowFajrTime,
        };
      }
    }

    return null;
  };

  const getActiveSalahWindow = (
    pt: PrayerTimes,
    sd: ScreenData,
  ): { prayerName: string; remainingSeconds: number } | null => {
    const now = getCurrentTimeInTimezone(sd.timeZone);

    const isFriday = now.getDay() === 5;
    const jummahLabel = sd.customizedNames?.jummah || "Jummah";

    const windows: Array<{
      prayerName: string;
      jamatTime: string;
      duration: number;
    }> = [
      {
        prayerName: sd.customizedNames?.fajr || "Fajr",
        jamatTime: pt.JamFajr,
        duration: sd.fajrLength,
      },
      // On Fridays, Jummah replaces Dhuhr — exclude Dhuhr so it cannot
      // match at the same time and shadow the Jummah windows
      ...(!isFriday
        ? [
            {
              prayerName: sd.customizedNames?.dhuhr || "Dhuhr",
              jamatTime: pt.JamDhuhr,
              duration: sd.dhuhrLength,
            },
          ]
        : []),
      ...(isFriday && pt.Jummah1 && pt.Jummah1 !== "--:--"
        ? [
            {
              prayerName: jummahLabel,
              jamatTime: pt.Jummah1,
              duration: sd.jummahLength,
            },
          ]
        : []),
      ...(isFriday && pt.Jummah2 && pt.Jummah2 !== "--:--"
        ? [
            {
              prayerName: `${jummahLabel} 2`,
              jamatTime: pt.Jummah2,
              duration: 15 * 60, // as getPrayerLength
            },
          ]
        : []),
      ...(isFriday && pt.Jummah3 && pt.Jummah3 !== "--:--"
        ? [
            {
              prayerName: `${jummahLabel} 3`,
              jamatTime: pt.Jummah3,
              duration: 15 * 60, // as getPrayerLength
            },
          ]
        : []),
      ...(isFriday && pt.Jummah4 && pt.Jummah4 !== "--:--"
        ? [
            {
              prayerName: `${jummahLabel} 4`,
              jamatTime: pt.Jummah4,
              duration: 15 * 60, // as getPrayerLength
            },
          ]
        : []),
      {
        prayerName: sd.customizedNames?.asar || "Asar",
        jamatTime: pt.JamAsar,
        duration: sd.asarLength,
      },
      {
        prayerName: sd.customizedNames?.maghrib || "Maghrib",
        jamatTime: pt.JamMaghrib,
        duration: sd.maghribLength,
      },
      {
        prayerName: sd.customizedNames?.isha || "Isha",
        jamatTime: pt.JamEsha,
        duration: sd.ishaLength,
      },
    ];

    let activeWindow: {
      prayerName: string;
      start: Date;
      end: Date;
    } | null = null;

    for (const windowInfo of windows) {
      if (!windowInfo.jamatTime || windowInfo.jamatTime === "--:--") continue;

      const durationSeconds = Math.max(0, windowInfo.duration || 0);
      if (durationSeconds <= 0) continue;

      const startTime = parseTimeToDate(windowInfo.jamatTime);
      if (!startTime) continue;

      const endTime = new Date(startTime.getTime() + durationSeconds * 1000);
      if (now < startTime || now > endTime) continue;

      if (!activeWindow || startTime.getTime() > activeWindow.start.getTime()) {
        activeWindow = {
          prayerName: windowInfo.prayerName,
          start: startTime,
          end: endTime,
        };
      }
    }

    if (!activeWindow) {
      return null;
    }

    return {
      prayerName: activeWindow.prayerName,
      remainingSeconds: Math.max(
        0,
        Math.floor((activeWindow.end.getTime() - now.getTime()) / 1000),
      ),
    };
  };

  // Whether a prayer's time element has moved on to tomorrow's time (for tomorrowTag_ elements);
  // mirrors the rollover in getPrayerTimeValue below.
  const isShowingTomorrow = (prayer: string): boolean => {
    if (prayer === "sunrise") {
      return getSunriseTimeDisplay(
        currentTimeStr,
        prayerTimes.Sunrise || "--:--",
        tomorrowPrayerTimes?.Sunrise,
        screenData.sunriseMakroohPeriod || 0,
      ).isTomorrow;
    }
    if (prayer === "dhuhr") {
      const now = new Date();
      if (
        now.getDay() === 5 &&
        getJummahDisplayInfo(now, currentTimeStr, prayerTimes, screenData)
          .showJummah
      ) {
        return false;
      }
    }
    const jamat: Record<string, [string | undefined, string | undefined, number | undefined]> = {
      fajr: [prayerTimes.JamFajr, tomorrowPrayerTimes?.JamFajr, screenData.fajrLength],
      dhuhr: [prayerTimes.JamDhuhr, tomorrowPrayerTimes?.JamDhuhr, screenData.dhuhrLength],
      asar: [prayerTimes.JamAsar, tomorrowPrayerTimes?.JamAsar, screenData.asarLength],
      maghrib: [prayerTimes.JamMaghrib, tomorrowPrayerTimes?.JamMaghrib, screenData.maghribLength],
      isha: [prayerTimes.JamEsha, tomorrowPrayerTimes?.JamEsha, screenData.ishaLength],
    };
    const times = jamat[prayer];
    return !!times && !!getJamatTimeDisplay(currentTimeStr, ...times)?.isTomorrow;
  };

  // Helper function to get prayer time value with tomorrow's fallback and custom names
  const getPrayerTimeValue = (
    elementId: string,
    component?: any, // Add component parameter to access customText
  ): string | React.ReactElement => {
    const timeFormat = {
      hour12: screenData.use12HourFormat || false,
      hour: "2-digit",
      minute: "2-digit",
    } as const;

    switch (elementId) {
      case "fajr": {
        const fajrDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamFajr,
          tomorrowPrayerTimes?.JamFajr,
          screenData.fajrLength,
        );
        return formatTimeWithPeriod(
          fajrDisplay?.time || "--:--",
          screenData.use12HourFormat,
          false, // No AM/PM for prayer times
        );
      }
      case "dhuhr": {
        // Handle Jummah logic similar to LandscapeLayout
        const currentDate = new Date();
        const isFriday = currentDate.getDay() === 5;

        if (isFriday) {
          const jummahInfo = getJummahDisplayInfo(
            currentDate,
            currentTimeStr,
            prayerTimes,
            screenData,
          );

          if (jummahInfo.showJummah) {
            return formatTimeWithPeriod(
              jummahInfo.jummahTime,
              screenData.use12HourFormat,
              false, // No AM/PM for prayer times
            );
          }
        }

        // Regular Dhuhr with duration consideration
        const dhuhrDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamDhuhr,
          tomorrowPrayerTimes?.JamDhuhr,
          screenData.dhuhrLength,
        );
        return formatTimeWithPeriod(
          dhuhrDisplay?.time || "--:--",
          screenData.use12HourFormat,
          false, // No AM/PM for prayer times
        );
      }
      case "asar": {
        const asarDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamAsar,
          tomorrowPrayerTimes?.JamAsar,
          screenData.asarLength,
        );
        return formatTimeWithPeriod(
          asarDisplay?.time || "--:--",
          screenData.use12HourFormat,
          false, // No AM/PM for prayer times
        );
      }
      case "maghrib": {
        const maghribDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamMaghrib,
          tomorrowPrayerTimes?.JamMaghrib,
          screenData.maghribLength,
        );
        return formatTimeWithPeriod(
          maghribDisplay?.time || "--:--",
          screenData.use12HourFormat,
          false, // No AM/PM for prayer times
        );
      }
      case "isha": {
        const ishaDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamEsha,
          tomorrowPrayerTimes?.JamEsha,
          screenData.ishaLength,
        );
        return formatTimeWithPeriod(
          ishaDisplay?.time || "--:--",
          screenData.use12HourFormat,
          false, // No AM/PM for prayer times
        );
      }
      case "sehriEnd": {
        const sehriDisplay = getSpecialTimeDisplay(
          currentTimeStr,
          prayerTimes.SehriEnd,
          tomorrowPrayerTimes?.SehriEnd,
        );
        return sehriDisplay
          ? formatTimeWithPeriod(
              sehriDisplay.time || "--:--",
              screenData.use12HourFormat,
              false, // No AM/PM for prayer times
            )
          : "--:--";
      }
      case "sunrise": {
        const sunriseDisplay = getSunriseTimeDisplay(
          currentTimeStr,
          prayerTimes.Sunrise || "--:--",
          tomorrowPrayerTimes?.Sunrise,
          screenData.sunriseMakroohPeriod || 0,
        );
        return formatTimeWithPeriod(
          sunriseDisplay.time,
          screenData.use12HourFormat,
          false, // No AM/PM for prayer times
        );
      }
      case "noon": {
        const noonDisplay = getSpecialTimeDisplay(
          currentTimeStr,
          prayerTimes.Noon,
          tomorrowPrayerTimes?.Noon,
        );
        return noonDisplay
          ? formatTimeWithPeriod(
              noonDisplay.time || "--:--",
              screenData.use12HourFormat,
              false, // No AM/PM for prayer times
            )
          : formatTimeWithPeriod("12:00", screenData.use12HourFormat, false);
      }
      case "jumuah1": {
        const jumuah1Display = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.Jummah1,
          tomorrowPrayerTimes?.Jummah1,
          screenData.jummahLength,
        );
        const time = jumuah1Display?.time;
        return time && time !== "--:--" && time !== ""
          ? /^\d{1,2}:\d{2}/.test(time)
            ? formatTimeWithPeriod(
                time,
                screenData.use12HourFormat,
                false, // No AM/PM for prayer times
              )
            : time
          : "--:--";
      }
      case "jumuah2": {
        const jumuah2Display = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.Jummah2,
          tomorrowPrayerTimes?.Jummah2,
          screenData.jummahLength,
        );
        const time = jumuah2Display?.time;
        return time && time !== "--:--" && time !== ""
          ? /^\d{1,2}:\d{2}/.test(time)
            ? formatTimeWithPeriod(
                time,
                screenData.use12HourFormat,
                false, // No AM/PM for prayer times
              )
            : time
          : "--:--";
      }
      case "begFajr": {
        const nextTime = getNextPrayerTime(
          PRAYER_TYPES.FAJR,
          prayerTimes,
          tomorrowPrayerTimes,
          currentTimeStr,
        );
        return formatTimeWithPeriod(
          nextTime.time || "--:--",
          screenData.use12HourFormat,
          false, // No AM/PM for prayer times
        );
      }
      case "begDhuhr": {
        const nextTime = getNextPrayerTime(
          PRAYER_TYPES.DHUHR,
          prayerTimes,
          tomorrowPrayerTimes,
          currentTimeStr,
        );
        return formatTimeWithPeriod(
          nextTime.time || "--:--",
          screenData.use12HourFormat,
          false, // No AM/PM for prayer times
        );
      }
      case "begAsar": {
        const nextTime = getNextPrayerTime(
          PRAYER_TYPES.ASAR,
          prayerTimes,
          tomorrowPrayerTimes,
          currentTimeStr,
        );
        return formatTimeWithPeriod(
          nextTime.time || "--:--",
          screenData.use12HourFormat,
          false, // No AM/PM for prayer times
        );
      }
      case "begAsar2":
        return formatTimeWithPeriod(
          prayerTimes.BegAsar2 || "--:--",
          screenData.use12HourFormat,
          false, // No AM/PM for prayer times
        );
      case "begMaghrib": {
        const nextTime = getNextPrayerTime(
          PRAYER_TYPES.MAGHRIB,
          prayerTimes,
          tomorrowPrayerTimes,
          currentTimeStr,
        );
        return formatTimeWithPeriod(
          nextTime.time || "--:--",
          screenData.use12HourFormat,
          false, // No AM/PM for prayer times
        );
      }
      case "begEsha": {
        const nextTime = getNextPrayerTime(
          PRAYER_TYPES.ISHA,
          prayerTimes,
          tomorrowPrayerTimes,
          currentTimeStr,
        );
        return formatTimeWithPeriod(
          nextTime.time || "--:--",
          screenData.use12HourFormat,
          false, // No AM/PM for prayer times
        );
      }
      case "currentTime":
        return screenData.use12HourFormat
          ? (() => {
              const hours = currentTime.getHours();
              const minutes = currentTime.getMinutes();
              const hour12 = hours % 12 || 12;
              const formattedTime = `${hour12}:${minutes
                .toString()
                .padStart(2, "0")}`;
              return formattedTime;
            })()
          : currentTime.toLocaleTimeString("en-GB", timeFormat);
      case "currentPeriod":
        return screenData.use12HourFormat
          ? (() => {
              const hours = currentTime.getHours();
              const period = hours >= 12 ? "PM" : "AM";
              return period;
            })()
          : ""; // No period for 24-hour format
      case "currentSeconds":
        return currentTime.getSeconds().toString().padStart(2, "0");
      case "hijriDate":
        return formattedIslamicDate;
      case "gregorianDate":
        return formattedGregorianDate;
      case "masjidName":
        return (
          <FitText>
            {screenData.masjidName || ""}
            {verified && <VerifiedBadge />}
          </FitText>
        );
      case "jumuahTimes": {
        // Every Jumu'ah the masjid holds; on Fridays the current/next one is picked out in the
        // masjid's colour once Jumu'ah is the next salah (not before Fajr), and finished ones
        // are dimmed.
        const now = new Date();
        const friday =
          now.getDay() === 5
            ? getJummahDisplayInfo(now, currentTimeStr, prayerTimes, screenData)
            : null;
        const jumuahIsNext = nextPrayer === 1;
        const times = [
          prayerTimes.Jummah1,
          prayerTimes.Jummah2,
          prayerTimes.Jummah3,
          prayerTimes.Jummah4,
        ]
          .map((time, i) => ({ time, number: i + 1 }))
          .filter(({ time }) => time && time !== "--:--");
        return (
          <FitText>
            {times.map(({ time, number }, i) => (
              <React.Fragment key={number}>
                {i > 0 && " · "}
                <span
                  style={
                    jumuahIsNext && friday?.showJummah && number === friday.jummahNumber
                      ? {
                          background: "var(--home-lit, var(--masjid-colour))",
                          color: "var(--home-lit-ink, var(--masjid-colour-ink))",
                          borderRadius: "0.12em",
                          padding: "0 0.18em",
                        }
                      : friday && (!friday.showJummah || number < friday.jummahNumber)
                        ? { opacity: 0.4 }
                        : undefined
                  }
                >
                  {formatTimeWithPeriod(time, screenData.use12HourFormat, false)}
                </span>
              </React.Fragment>
            ))}
          </FitText>
        );
      }
      case "masjidPhoto":
        return (
          <CachedImage
            src={`${runtimeConfig.storageUrl}/masaajidimages/${screenData.masjidId}.png`}
            alt=""
            className="masjid-photo"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "50%",
              border: "6px solid var(--home-lit, var(--masjid-colour))",
              boxSizing: "border-box",
            }}
          />
        );
      case "makroohSunrise": {
        const customText = component?.customText || component?.CustomText;
        const showCountdownTimer =
          (component?.showCountdownTimer ??
            component?.ShowCountdownTimer ??
            true) !== false;
        const countdownLabelText =
          component?.countdownLabelText ??
          component?.CountdownLabelText ??
          "Time remaining:";
        const countdownOnNewLine =
          (component?.countdownOnNewLine ??
            component?.CountdownOnNewLine ??
            true) !== false;
        if (isMakroohTime && makroohReason === "sunrise") {
          const countdown =
            showCountdownTimer && makroohSecondsLeft > 0
              ? formatTime(makroohSecondsLeft)
              : "";
          const countdownLine = countdown
            ? countdownLabelText === ""
              ? countdown
              : `${countdownLabelText} ${countdown}`
            : "";
          const baseMessage =
            customText ||
            `Prayer is Makrooh (${
              screenData.customizedNames?.sunrise || "Sunrise"
            })`;
          if (!countdownLine) return baseMessage;
          return countdownOnNewLine ? (
            <>
              {baseMessage}
              <br />
              <br />
              {countdownLine}
            </>
          ) : (
            <>
              {baseMessage} {countdownLine}
            </>
          );
        }
        return "";
      }
      case "makroohZawwal": {
        const customText = component?.customText || component?.CustomText;
        const showCountdownTimer =
          (component?.showCountdownTimer ??
            component?.ShowCountdownTimer ??
            true) !== false;
        const countdownLabelText =
          component?.countdownLabelText ??
          component?.CountdownLabelText ??
          "Time remaining:";
        const countdownOnNewLine =
          (component?.countdownOnNewLine ??
            component?.CountdownOnNewLine ??
            true) !== false;
        if (isMakroohTime && makroohReason === "zawwal") {
          const countdown =
            showCountdownTimer && makroohSecondsLeft > 0
              ? formatTime(makroohSecondsLeft)
              : "";
          const countdownLine = countdown
            ? countdownLabelText === ""
              ? countdown
              : `${countdownLabelText} ${countdown}`
            : "";
          const baseMessage =
            customText ||
            `Prayer is Makrooh (${screenData.customizedNames?.noon || "Noon"})`;
          if (!countdownLine) return baseMessage;
          return countdownOnNewLine ? (
            <>
              {baseMessage}
              <br />
              {countdownLine}
            </>
          ) : (
            <>
              {baseMessage} {countdownLine}
            </>
          );
        }
        return "";
      }
      case "makroohSunset": {
        const customText = component?.customText || component?.CustomText;
        const showCountdownTimer =
          (component?.showCountdownTimer ??
            component?.ShowCountdownTimer ??
            true) !== false;
        const countdownLabelText =
          component?.countdownLabelText ??
          component?.CountdownLabelText ??
          "Time remaining:";
        const countdownOnNewLine =
          (component?.countdownOnNewLine ??
            component?.CountdownOnNewLine ??
            true) !== false;
        if (isMakroohTime && makroohReason === "sunset") {
          const countdown =
            showCountdownTimer && makroohSecondsLeft > 0
              ? formatTime(makroohSecondsLeft)
              : "";
          const countdownLine = countdown
            ? countdownLabelText === ""
              ? countdown
              : `${countdownLabelText} ${countdown}`
            : "";
          const baseMessage =
            customText ||
            `Prayer is Makrooh (${
              screenData.customizedNames?.sunset || "Sunset"
            })`;
          if (!countdownLine) return baseMessage;
          return countdownOnNewLine ? (
            <>
              {baseMessage}
              <br />
              {countdownLine}
            </>
          ) : (
            <>
              {baseMessage} {countdownLine}
            </>
          );
        }
        return "";
      }
      case "conditionalOverlay1": {
        const overlaySettings =
          component?.conditionalOverlay || component?.ConditionalOverlay;

        if (overlaySettings) {
          const isActive = isConditionalOverlayActive(overlaySettings);
          if (isActive) {
            const activeText =
              overlaySettings.ActiveText ||
              overlaySettings.activeText ||
              "Active";
            return applyPrayerNameTokens(activeText, screenData.customizedNames);
          } else {
            const defaultText =
              overlaySettings.DefaultText || overlaySettings.defaultText || "";
            // Return empty string instead of falling back to element ID
            return applyPrayerNameTokens(defaultText, screenData.customizedNames);
          }
        } else {
          // Return empty string when no overlay settings exist
          return "";
        }
      }
      case "adhanFajr": {
        const adhanSettings =
          component?.adhanDisplaySettings || component?.AdhanDisplaySettings;
        const displayMode =
          adhanSettings?.displayMode || adhanSettings?.DisplayMode || "time";
        const offset = screenData.fajrAdhanOffset || 0;
        if (displayMode === "offset") return offset === 0 ? "0" : `-${offset}`;
        const fajrDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamFajr,
          tomorrowPrayerTimes?.JamFajr,
          screenData.fajrLength,
        );
        const baseTime = fajrDisplay?.time;
        if (!baseTime || baseTime === "--:--") return "--:--";
        return formatTimeWithPeriod(
          subtractMinutesFromTime(baseTime, offset),
          screenData.use12HourFormat,
          false,
        );
      }
      case "adhanDhuhr": {
        const adhanSettings =
          component?.adhanDisplaySettings || component?.AdhanDisplaySettings;
        const displayMode =
          adhanSettings?.displayMode || adhanSettings?.DisplayMode || "time";
        const offset = screenData.dhuhrAdhanOffset || 0;
        if (displayMode === "offset") return offset === 0 ? "0" : `-${offset}`;
        const dhuhrDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamDhuhr,
          tomorrowPrayerTimes?.JamDhuhr,
          screenData.dhuhrLength,
        );
        const baseTime = dhuhrDisplay?.time;
        if (!baseTime || baseTime === "--:--") return "--:--";
        return formatTimeWithPeriod(
          subtractMinutesFromTime(baseTime, offset),
          screenData.use12HourFormat,
          false,
        );
      }
      case "adhanAsar": {
        const adhanSettings =
          component?.adhanDisplaySettings || component?.AdhanDisplaySettings;
        const displayMode =
          adhanSettings?.displayMode || adhanSettings?.DisplayMode || "time";
        const offset = screenData.asarAdhanOffset || 0;
        if (displayMode === "offset") return offset === 0 ? "0" : `-${offset}`;
        const asarDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamAsar,
          tomorrowPrayerTimes?.JamAsar,
          screenData.asarLength,
        );
        const baseTime = asarDisplay?.time;
        if (!baseTime || baseTime === "--:--") return "--:--";
        return formatTimeWithPeriod(
          subtractMinutesFromTime(baseTime, offset),
          screenData.use12HourFormat,
          false,
        );
      }
      case "adhanMaghrib": {
        const adhanSettings =
          component?.adhanDisplaySettings || component?.AdhanDisplaySettings;
        const displayMode =
          adhanSettings?.displayMode || adhanSettings?.DisplayMode || "time";
        const offset = screenData.maghribAdhanOffset || 0;
        if (displayMode === "offset") return offset === 0 ? "0" : `-${offset}`;
        const maghribDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamMaghrib,
          tomorrowPrayerTimes?.JamMaghrib,
          screenData.maghribLength,
        );
        const baseTime = maghribDisplay?.time;
        if (!baseTime || baseTime === "--:--") return "--:--";
        return formatTimeWithPeriod(
          subtractMinutesFromTime(baseTime, offset),
          screenData.use12HourFormat,
          false,
        );
      }
      case "adhanIsha": {
        const adhanSettings =
          component?.adhanDisplaySettings || component?.AdhanDisplaySettings;
        const displayMode =
          adhanSettings?.displayMode || adhanSettings?.DisplayMode || "time";
        const offset = screenData.ishaAdhanOffset || 0;
        if (displayMode === "offset") return offset === 0 ? "0" : `-${offset}`;
        const ishaDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamEsha,
          tomorrowPrayerTimes?.JamEsha,
          screenData.ishaLength,
        );
        const baseTime = ishaDisplay?.time;
        if (!baseTime || baseTime === "--:--") return "--:--";
        return formatTimeWithPeriod(
          subtractMinutesFromTime(baseTime, offset),
          screenData.use12HourFormat,
          false,
        );
      }
      case "adhanJummah": {
        const adhanSettings =
          component?.adhanDisplaySettings || component?.AdhanDisplaySettings;
        const displayMode =
          adhanSettings?.displayMode || adhanSettings?.DisplayMode || "time";
        const offset = screenData.jummahAdhanOffset || 0;
        if (displayMode === "offset") return offset === 0 ? "0" : `-${offset}`;
        // Use Jummah1 time on Fridays, otherwise fall back to Dhuhr jamaah
        const currentDate = new Date();
        const isFriday = currentDate.getDay() === 5;
        let baseTime: string | undefined;
        if (isFriday) {
          const jummahDisplay = getJamatTimeDisplay(
            currentTimeStr,
            prayerTimes.Jummah1,
            tomorrowPrayerTimes?.Jummah1,
            screenData.jummahLength,
          );
          baseTime = jummahDisplay?.time;
        }
        if (!baseTime || baseTime === "--:--") {
          const dhuhrDisplay = getJamatTimeDisplay(
            currentTimeStr,
            prayerTimes.JamDhuhr,
            tomorrowPrayerTimes?.JamDhuhr,
            screenData.dhuhrLength,
          );
          baseTime = dhuhrDisplay?.time;
        }
        if (!baseTime || baseTime === "--:--") return "--:--";
        return formatTimeWithPeriod(
          subtractMinutesFromTime(baseTime, offset),
          screenData.use12HourFormat,
          false,
        );
      }
      default:
        // Handle custom text elements from designer
        if (elementId?.startsWith("customText_")) {
          const customTextContent =
            component?.customText || component?.CustomText || "";

          // {fajr} … {jummah} become the masjid's own names; other text is unchanged.
          return applyPrayerNameTokens(customTextContent, screenData.customizedNames);
        }

        // For conditional overlays without specific handling, check if they have overlay settings
        if (elementId?.startsWith("conditionalOverlay")) {
          const overlaySettings =
            component?.conditionalOverlay || component?.ConditionalOverlay;

          if (overlaySettings) {
            const isActive = isConditionalOverlayActive(overlaySettings);
            if (isActive) {
              const activeText =
                overlaySettings.ActiveText ||
                overlaySettings.activeText ||
                "Active";
              return applyPrayerNameTokens(activeText, screenData.customizedNames);
            } else {
              const defaultText =
                overlaySettings.DefaultText ||
                overlaySettings.defaultText ||
                "";
              return applyPrayerNameTokens(defaultText, screenData.customizedNames);
            }
          } else {
            return "";
          }
        }

        if (elementId?.startsWith("tomorrowTag_")) {
          return isShowingTomorrow(elementId.slice("tomorrowTag_".length))
            ? component?.customText || component?.CustomText || ""
            : "";
        }

        // For other elements, return the element ID as fallback
        return elementId;
    }
  };

  // Helper function to get clock time for analog clocks (updated to match text element logic)
  const getClockTime = (clockId: string): string => {
    switch (clockId) {
      case "fajrClock": {
        const fajrDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamFajr,
          tomorrowPrayerTimes?.JamFajr,
          screenData.fajrLength,
        );
        return fajrDisplay?.time || prayerTimes.JamFajr || "04:30";
      }
      case "dhuhrClock": {
        // Handle Jummah logic similar to text elements
        const currentDate = new Date();
        const isFriday = currentDate.getDay() === 5;

        if (isFriday) {
          const jummahInfo = getJummahDisplayInfo(
            currentDate,
            currentTimeStr,
            prayerTimes,
            screenData,
          );

          if (jummahInfo.showJummah) {
            return jummahInfo.jummahTime ?? "";
          }
        }

        // Regular Dhuhr with duration consideration
        const dhuhrDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamDhuhr,
          tomorrowPrayerTimes?.JamDhuhr,
          screenData.dhuhrLength,
        );
        return dhuhrDisplay?.time || prayerTimes.JamDhuhr || "12:30";
      }
      case "asarClock": {
        const asarDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamAsar,
          tomorrowPrayerTimes?.JamAsar,
          screenData.asarLength,
        );
        return asarDisplay?.time || prayerTimes.JamAsar || "15:45";
      }
      case "maghribClock": {
        const maghribDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamMaghrib,
          tomorrowPrayerTimes?.JamMaghrib,
          screenData.maghribLength,
        );
        return maghribDisplay?.time || prayerTimes.JamMaghrib || "18:30";
      }
      case "ishaClock": {
        const ishaDisplay = getJamatTimeDisplay(
          currentTimeStr,
          prayerTimes.JamEsha,
          tomorrowPrayerTimes?.JamEsha,
          screenData.ishaLength,
        );
        return ishaDisplay?.time || prayerTimes.JamEsha || "20:15";
      }
      default:
        return "12:00";
    }
  };

  // Render individual element based on type
  const renderElement = (component: any, index: number) => {
    // Handle both property name cases
    const id = component.Id || component.id;

    // Determine visibility with safe default to true
    const visibleVal =
      component.Visible !== undefined
        ? component.Visible
        : component.visible !== undefined
          ? component.visible
          : undefined;
    const isVisible = visibleVal === undefined ? true : !!visibleVal;

    if (!isVisible) {
      return null;
    }

    // Check if this is a playlist display component
    if (isPlaylistDisplayElement(component)) {
      const playlistSettings =
        component.playlistDisplay || component.PlaylistDisplay || {};
      const position = component.Position || component.position;
      const size = component.Size || component.size;

      // Extract component-level display settings
      const playlistDisplaySettings =
        component.playlistDisplaySettings ||
        component.PlaylistDisplaySettings ||
        {};

      // Extract position
      const positionX = position?.X ?? position?.x ?? 0;
      const positionY = position?.Y ?? position?.y ?? 0;

      // Extract size
      const width = size?.Width ?? size?.width ?? 300;
      const height = size?.Height ?? size?.height ?? 400;

      // Determine content mode: "custom" means use component settings, "all" means use screen settings
      const contentMode =
        playlistDisplaySettings.contentMode ||
        playlistDisplaySettings.ContentMode ||
        "all";

      // Create filtered screenData if in custom mode
      // Component settings act as a FILTER on screen settings (AND logic) for most features
      // Exception: showUpcomingEvents can be independently enabled in custom mode
      let filteredScreenData = processedScreenData;
      if (contentMode === "custom") {
        // Component preferences (what this component wants to show)
        const componentWantsPlaylists =
          playlistDisplaySettings.showPlaylists ??
          playlistDisplaySettings.ShowPlaylists ??
          true;
        const componentWantsUpcoming =
          playlistDisplaySettings.showUpcoming ??
          playlistDisplaySettings.ShowUpcoming ??
          true;
        const componentWantsUpcomingEvents =
          playlistDisplaySettings.showUpcomingEvents ??
          playlistDisplaySettings.ShowUpcomingEvents ??
          false; // Default to false - only show if explicitly enabled
        const selectedPlaylistIdsRaw =
          playlistDisplaySettings.selectedPlaylistIds ??
          playlistDisplaySettings.SelectedPlaylistIds ??
          [];
        const selectedPlaylistRules = Array.from(
          new Set(
            (Array.isArray(selectedPlaylistIdsRaw)
              ? selectedPlaylistIdsRaw
              : String(selectedPlaylistIdsRaw)
                  .split(",")
                  .map((token) => token.trim())
                  .filter((token) => token.length > 0)
            )
              .map((value) => {
                if (typeof value === "number") {
                  if (!Number.isFinite(value) || value === 0) {
                    return null;
                  }

                  const normalized = Math.abs(Math.trunc(value));
                  return value < 0 ? -normalized : normalized;
                }

                const token = String(value).trim();
                if (!token) {
                  return null;
                }

                const isExclude = token.startsWith("!");
                const numericToken = isExclude ? token.slice(1).trim() : token;
                const parsed = Number.parseInt(numericToken, 10);
                if (!Number.isFinite(parsed) || parsed === 0) {
                  return null;
                }

                const normalized = Math.abs(parsed);
                return isExclude ? -normalized : normalized;
              })
              .filter((id): id is number => id !== null && Number.isFinite(id)),
          ),
        );
        const includedPlaylistIds = selectedPlaylistRules.filter(
          (id) => id > 0,
        );
        const excludedPlaylistIdSet = new Set(
          selectedPlaylistRules
            .filter((id) => id < 0)
            .map((id) => Math.abs(id)),
        );

        // Effective settings: component can only show what screen has enabled
        // Screen setting AND component setting
        const effectiveShowUpcoming =
          processedScreenData.showNextSalahChanges && componentWantsUpcoming;
        // For upcoming events calendar, component setting overrides screen setting in custom mode
        // This allows showing the calendar even if not enabled at screen level
        const effectiveShowUpcomingEvents = componentWantsUpcomingEvents;

        // Filter playlists based on settings
        const filteredPlaylists = processedScreenData.playlists?.filter(
          (playlist) => {
            if (!componentWantsPlaylists) {
              return false;
            }

            if (excludedPlaylistIdSet.has(playlist.id)) {
              return false;
            }

            // Empty include selection means include all remaining playlists.
            if (includedPlaylistIds.length === 0) {
              return true;
            }

            return includedPlaylistIds.includes(playlist.id);
          },
        );

        filteredScreenData = {
          ...processedScreenData,
          playlists: filteredPlaylists || [],
          showEvents: false, // Decommissioned
          showNextSalahChanges: effectiveShowUpcoming,
          showUpcomingEvents: effectiveShowUpcomingEvents,
          // Labbaik counts as playlist content, so it follows "Show Playlists"
          showLabbaik: !!processedScreenData.showLabbaik && componentWantsPlaylists,
          showLabbaikEvents: !!processedScreenData.showLabbaik,
        };
      }

      // Per-component home page gap: read from playlistDisplaySettings JSON blob
      const componentGapEnabled: boolean | undefined =
        playlistDisplaySettings.showHomePageBetweenCycles ??
        playlistDisplaySettings.ShowHomePageBetweenCycles;
      const componentGapDurationSeconds: number | undefined =
        playlistDisplaySettings.homePageDisplayDurationSeconds ??
        playlistDisplaySettings.HomePageDisplayDurationSeconds;

      // When a per-component gap is configured, suppress the screen-level flag so
      // this component doesn't also join the shared coordinator barrier.
      if (componentGapEnabled !== undefined) {
        filteredScreenData = {
          ...filteredScreenData,
          showHomePageBetweenCycles: false,
        };
      }

      return (
        <div
          key={id}
          style={{
            position: "absolute",
            left: `${positionX}px`,
            top: `${positionY}px`,
            zIndex: 100,
          }}
        >
          <SimplePlaylistDisplayComponent
            width={width}
            height={height}
            className="custom-layout-playlist-display"
            participantId={`${id || "playlist"}-${index}`}
            componentGapEnabled={componentGapEnabled}
            componentGapDurationSeconds={componentGapDurationSeconds}
            // Pass real data to the component
            prayerTimes={prayerTimes}
            tomorrowPrayerTimes={tomorrowPrayerTimes}
            screenData={filteredScreenData}
            masjidId={masjidId}
            prayerState={prayerState}
            prevFridayJummah1={prevFridayJummah1}
            prevFridayJummah2={prevFridayJummah2}
            prevFridayJummah3={prevFridayJummah3}
            prevFridayJummah4={prevFridayJummah4}
          />
        </div>
      );
    }

    // Check if this is an image asset component
    if (isImageAssetElement(component)) {
      const imageSettings =
        component.imageAssetSettings || component.ImageAssetSettings || {};
      const position = component.Position || component.position;
      const size = component.Size || component.size;

      // Extract image asset specific settings
      const assetUrl = imageSettings.assetUrl || imageSettings.AssetUrl || "";

      // Don't render if no URL is provided
      if (!assetUrl) {
        return null;
      }

      // Extract position
      const positionX = position?.X ?? position?.x ?? 0;
      const positionY = position?.Y ?? position?.y ?? 0;

      // Extract size
      const width = size?.Width ?? size?.width ?? 200;
      const height = size?.Height ?? size?.height ?? 200;

      const objectFit =
        imageSettings.objectFit || imageSettings.ObjectFit || "contain";
      const opacity = imageSettings.opacity ?? imageSettings.Opacity ?? 1;
      const borderRadius =
        imageSettings.borderRadius ?? imageSettings.BorderRadius ?? 0;

      // Resolve the URL using runtimeConfig
      const resolvedUrl = assetUrl.startsWith("http")
        ? assetUrl
        : runtimeConfig.getBlobUrl(assetUrl);

      return (
        <div
          key={id}
          style={{
            position: "absolute",
            left: `${positionX}px`,
            top: `${positionY}px`,
            width: `${width}px`,
            height: `${height}px`,
            opacity,
            borderRadius: `${borderRadius}px`,
            overflow: "hidden",
          }}
        >
          <img
            src={resolvedUrl}
            alt={imageSettings.assetName || imageSettings.AssetName || "Image"}
            style={{
              width: "100%",
              height: "100%",
              objectFit: objectFit as
                | "cover"
                | "contain"
                | "fill"
                | "none"
                | "scale-down",
            }}
            onError={(e) => {
              // Hide image on error and show nothing (transparent)
              const target = e.target as HTMLImageElement;
              target.style.display = "none";
            }}
          />
        </div>
      );
    }

    // Check if this is a standalone countdown display element
    if (isCountdownDisplayElement(component)) {
      const countdownTextColor = screenData.countdownTextColor || "#FF0000";
      const cdSettings =
        component.countdownDisplaySettings ||
        component.CountdownDisplaySettings ||
        {};
      const position = component.Position || component.position;
      const style = component.Style || component.style || {};
      const size = component.Size || component.size;

      const hideWhenInactive =
        cdSettings.hideWhenInactive ?? cdSettings.HideWhenInactive ?? false;
      const countdownTarget =
        cdSettings.countdownTarget || cdSettings.CountdownTarget || "jamaah";
      const countdownFormat =
        cdSettings.countdownFormat || cdSettings.CountdownFormat || "hh:mm:ss";
      const duringSalahBehavior =
        cdSettings.duringSalahBehavior ||
        cdSettings.DuringSalahBehavior ||
        "none";
      const preSalahLabel =
        cdSettings.preSalahLabel || cdSettings.PreSalahLabel || "";
      const duringSalahLabel =
        cdSettings.duringSalahLabel || cdSettings.DuringSalahLabel || "";
      const labelStyle = cdSettings.labelStyle || cdSettings.LabelStyle || {};

      // Extract position
      const positionX = position?.X ?? position?.x ?? 0;
      const positionY = position?.Y ?? position?.y ?? 0;

      // Extract size
      const width = size?.Width ?? size?.width ?? 300;
      const height = size?.Height ?? size?.height ?? 80;

      // Extract style
      const fontSize = style?.FontSize ?? style?.fontSize ?? 48;
      const fontFamily = style?.FontFamily ?? style?.fontFamily ?? "Arial";
      const color = style?.Color ?? style?.color ?? countdownTextColor;
      const fontWeight =
        style?.FontWeight ??
        style?.fontWeight ??
        (style?.IsBold || style?.isBold ? 700 : 400);
      const textAlign = style?.TextAlign ?? style?.textAlign ?? "center";
      const labelTextAlign =
        labelStyle?.textAlign ?? labelStyle?.TextAlign ?? textAlign ?? "center";
      const labelFontSize = labelStyle?.fontSize ?? labelStyle?.FontSize ?? 24;
      const labelFontFamily =
        labelStyle?.fontFamily ?? labelStyle?.FontFamily ?? fontFamily;
      const labelColor = labelStyle?.color ?? labelStyle?.Color ?? "#ffffff";
      const labelFontWeight =
        labelStyle?.fontWeight ??
        labelStyle?.FontWeight ??
        ((labelStyle?.isBold ?? labelStyle?.IsBold) ? 700 : 400);

      // Find next prayer (with tomorrow fallback)
      const nextPrayer = findNextPrayer(
        prayerTimes,
        screenData,
        countdownTarget,
        tomorrowPrayerTimes,
      );
      const activeSalahWindow = getActiveSalahWindow(prayerTimes, screenData);
      const isInSalahWindow = !!activeSalahWindow;

      if (!nextPrayer && !isInSalahWindow && hideWhenInactive) {
        return null;
      }

      const now = getCurrentTimeInTimezone(screenData.timeZone);
      const baseCountdownSecs = nextPrayer
        ? Math.max(
            0,
            Math.floor((nextPrayer.time.getTime() - now.getTime()) / 1000),
          )
        : 0;
      let countdownSecs = baseCountdownSecs;

      if (isInSalahWindow) {
        if (duringSalahBehavior === "salahRemaining") {
          countdownSecs = activeSalahWindow?.remainingSeconds || 0;
        } else if (duringSalahBehavior === "pauseDuringSalah") {
          countdownSecs = 0;
        }
      }

      const countdownText = formatStandaloneCountdown(
        countdownSecs,
        countdownFormat,
      );

      const applyLabelTokens = (template: string, prayerName: string) =>
        (template || "").replace(/\{([A-Za-z]+)\}/g, (fullMatch, rawToken) => {
          const token = String(rawToken || "");
          const tokenLower = token.toLowerCase();

          if (tokenLower === "remaining") {
            return countdownText;
          }

          if (
            tokenLower === "prayer" ||
            tokenLower === "namaz" ||
            tokenLower === "salah"
          ) {
            return formatByTokenCase(prayerName || "", token);
          }

          return applyPrayerNameTokens(fullMatch, screenData.customizedNames);
        });

      const preSalahPrayerName = nextPrayer?.name || "";
      const duringSalahPrayerName =
        activeSalahWindow?.prayerName || preSalahPrayerName;
      const resolvedPreLabel = applyLabelTokens(
        preSalahLabel,
        preSalahPrayerName,
      );
      const resolvedDuringLabel = applyLabelTokens(
        duringSalahLabel,
        duringSalahPrayerName,
      );
      const labelText = isInSalahWindow
        ? resolvedDuringLabel
        : resolvedPreLabel;
      const hasLabel = !!labelText.trim();

      return (
        <div
          key={id}
          style={{
            position: "absolute",
            left: `${positionX}px`,
            top: `${positionY}px`,
            width: `${width}px`,
            height: `${height}px`,
            fontFamily,
            fontSize: `${fontSize}px`,
            color,
            fontWeight,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "center",
            overflow: "hidden",
            userSelect: "none",
            padding: "4px",
            boxSizing: "border-box",
          }}
        >
          {hasLabel && (
            <div
              style={{
                width: "100%",
                textAlign: labelTextAlign as any,
                fontFamily: labelFontFamily,
                fontSize: `${labelFontSize}px`,
                color: labelColor,
                fontWeight: labelFontWeight,
                lineHeight: 1.1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginBottom: "4px",
              }}
            >
              {labelText}
            </div>
          )}
          <div
            style={{
              width: "100%",
              textAlign: textAlign as any,
              fontFamily,
              fontSize: `${fontSize}px`,
              color,
              fontWeight,
              lineHeight: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {countdownText}
          </div>
        </div>
      );
    }

    // The next salah on its own, following the Masjid Clock's rules (Home's "Next prayer" screen)
    if (id && id.startsWith("nextPrayerBlock_")) {
      const position = component.Position || component.position;
      const style = component.Style || component.style || {};
      const size = component.Size || component.size;
      return (
        <NextPrayerBlock
          key={id}
          x={position?.X ?? position?.x ?? 0}
          y={position?.Y ?? position?.y ?? 0}
          width={size?.Width ?? size?.width ?? 1200}
          height={size?.Height ?? size?.height ?? 560}
          fontFamily={style?.FontFamily ?? style?.fontFamily ?? "Arial"}
          prayerTimes={prayerTimes}
          tomorrowPrayerTimes={tomorrowPrayerTimes}
          screenData={screenData}
        />
      );
    }

    // Check if this is a next prayer name element
    if (id && id.startsWith("nextPrayerName_")) {
      const position = component.Position || component.position;
      const style = component.Style || component.style || {};
      const size = component.Size || component.size;

      const positionX = position?.X ?? position?.x ?? 0;
      const positionY = position?.Y ?? position?.y ?? 0;
      const width = size?.Width ?? size?.width ?? 200;
      const height = size?.Height ?? size?.height ?? 60;
      const fontSize = style?.FontSize ?? style?.fontSize ?? 36;
      const fontFamily = style?.FontFamily ?? style?.fontFamily ?? "Arial";
      const color = style?.Color ?? style?.color ?? "#2e7d32";
      const fontWeight =
        style?.FontWeight ??
        style?.fontWeight ??
        (style?.IsBold || style?.isBold ? 700 : 400);
      const textAlign = style?.TextAlign ?? style?.textAlign ?? "center";
      const textCase = style?.TextCase ?? style?.textCase ?? "none";

      const nextPrayer = findNextPrayer(
        prayerTimes,
        screenData,
        "jamaah",
        tomorrowPrayerTimes,
      );
      const prayerName = nextPrayer ? nextPrayer.name : "—";

      return (
        <div
          key={id}
          style={{
            position: "absolute",
            left: `${positionX}px`,
            top: `${positionY}px`,
            width: `${width}px`,
            height: `${height}px`,
            fontFamily,
            fontSize: `${fontSize}px`,
            color,
            fontWeight,
            textAlign: textAlign as any,
            textTransform: textCase !== "none" ? (textCase as any) : undefined,
            display: "flex",
            alignItems: "center",
            justifyContent:
              textAlign === "left"
                ? "flex-start"
                : textAlign === "right"
                  ? "flex-end"
                  : "center",
            overflow: "hidden",
            userSelect: "none",
          }}
        >
          {prayerName}
        </div>
      );
    }

    // Check if this is a next prayer time element
    if (id && id.startsWith("nextPrayerTime_")) {
      const position = component.Position || component.position;
      const style = component.Style || component.style || {};
      const size = component.Size || component.size;

      const positionX = position?.X ?? position?.x ?? 0;
      const positionY = position?.Y ?? position?.y ?? 0;
      const width = size?.Width ?? size?.width ?? 200;
      const height = size?.Height ?? size?.height ?? 60;
      const fontSize = style?.FontSize ?? style?.fontSize ?? 36;
      const fontFamily = style?.FontFamily ?? style?.fontFamily ?? "Arial";
      const color = style?.Color ?? style?.color ?? "#1565c0";
      const fontWeight =
        style?.FontWeight ??
        style?.fontWeight ??
        (style?.IsBold || style?.isBold ? 700 : 400);
      const textAlign = style?.TextAlign ?? style?.textAlign ?? "center";
      const textCase = style?.TextCase ?? style?.textCase ?? "none";

      const nextPrayer = findNextPrayer(
        prayerTimes,
        screenData,
        "jamaah",
        tomorrowPrayerTimes,
      );
      // Format the jamaah time for display
      const prayerTimeText = nextPrayer
        ? formatTimeWithPeriod(
            nextPrayer.jamaahTime,
            screenData.use12HourFormat,
            false,
          )
        : "—";

      return (
        <div
          key={id}
          style={{
            position: "absolute",
            left: `${positionX}px`,
            top: `${positionY}px`,
            width: `${width}px`,
            height: `${height}px`,
            fontFamily,
            fontSize: `${fontSize}px`,
            color,
            fontWeight,
            textAlign: textAlign as any,
            textTransform: textCase !== "none" ? (textCase as any) : undefined,
            display: "flex",
            alignItems: "center",
            justifyContent:
              textAlign === "left"
                ? "flex-start"
                : textAlign === "right"
                  ? "flex-end"
                  : "center",
            overflow: "hidden",
            userSelect: "none",
          }}
        >
          {prayerTimeText}
        </div>
      );
    }

    // Check if this is a flexible time element
    if (isFlexibleTimeElement(component)) {
      const flexSettings =
        component.flexibleTimeSettings || component.FlexibleTimeSettings || {};
      const position = component.Position || component.position;
      const style = component.Style || component.style || {};
      const size = component.Size || component.size;

      // Extract settings
      const mode = flexSettings.mode || flexSettings.Mode || "mirror";
      const sourceField =
        flexSettings.sourceField || flexSettings.SourceField || "fajr";
      const offsetMinutes =
        flexSettings.offsetMinutes ?? flexSettings.OffsetMinutes ?? 0;
      const showTomorrowAfterPassed =
        flexSettings.showTomorrowAfterPassed ??
        flexSettings.ShowTomorrowAfterPassed ??
        true;

      // Extract position
      const positionX = position?.X ?? position?.x ?? 0;
      const positionY = position?.Y ?? position?.y ?? 0;

      // Extract size
      const width = size?.Width ?? size?.width ?? 180;
      const height = size?.Height ?? size?.height ?? 100;

      // Extract style
      const fontSize = style?.FontSize ?? style?.fontSize ?? 64;
      const fontFamily =
        style?.FontFamily ?? style?.fontFamily ?? "LCD Modern Bold";
      const color = style?.Color ?? style?.color ?? "#d4ac56";
      const fontWeight =
        style?.FontWeight ??
        style?.fontWeight ??
        (style?.IsBold || style?.isBold ? 700 : 400);

      // Helper function to calculate time based on source field
      const getFlexibleTimeValue = (): string => {
        // Map source fields to their corresponding prayer time values
        const getSourceTime = (field: string): string | null => {
          // Jamat times
          if (field === "fajr") return prayerTimes.JamFajr || null;
          if (field === "dhuhr") return prayerTimes.JamDhuhr || null;
          if (field === "asar") return prayerTimes.JamAsar || null;
          if (field === "maghrib") return prayerTimes.JamMaghrib || null;
          if (field === "isha") return prayerTimes.JamEsha || null;

          // Second jamaat times
          if (field === "fajr2") return prayerTimes.JamFajr2 || null;
          if (field === "dhuhr2") return prayerTimes.JamDhuhr2 || null;
          if (field === "asar2") return prayerTimes.JamAsar2 || null;
          if (field === "maghrib2") return prayerTimes.JamMaghrib2 || null;
          if (field === "isha2") return prayerTimes.JamEsha2 || null;

          // Beginning times (map to prayerTimes Beg properties)
          if (field === "begFajr") return prayerTimes.BegFajr || null;
          if (field === "begDhuhr") return prayerTimes.BegDhuhr || null;
          if (field === "begAsar") return prayerTimes.BegAsar || null;
          if (field === "begMaghrib") {
            // BegMaghrib may not be populated; fall back to Sunset (matches PRAYER_TIME_KEYS)
            const beg = prayerTimes.BegMaghrib;
            return beg && beg !== "--:--" ? beg : prayerTimes.Sunset || null;
          }
          if (field === "begEsha") return prayerTimes.BegEsha || null;

          // Jummah times
          if (field === "jumuah1") return prayerTimes.Jummah1 || null;
          if (field === "jumuah2") return prayerTimes.Jummah2 || null;
          if (field === "jumuah3") return prayerTimes.Jummah3 || null;
          if (field === "jumuah4") return prayerTimes.Jummah4 || null;
          if (field === "jumuah5") return prayerTimes.Jummah5 || null;

          // Special times
          if (field === "sehriEnd") return prayerTimes.SehriEnd || null;
          if (field === "sunrise") return prayerTimes.Sunrise || null;
          if (field === "noon") return prayerTimes.Noon || null;

          return null;
        };

        const getTomorrowSourceTime = (field: string): string | null => {
          if (!tomorrowPrayerTimes) return null;

          // Jamat times
          if (field === "fajr") return tomorrowPrayerTimes.JamFajr || null;
          if (field === "dhuhr") return tomorrowPrayerTimes.JamDhuhr || null;
          if (field === "asar") return tomorrowPrayerTimes.JamAsar || null;
          if (field === "maghrib")
            return tomorrowPrayerTimes.JamMaghrib || null;
          if (field === "isha") return tomorrowPrayerTimes.JamEsha || null;

          // Second jamaat times
          if (field === "fajr2") return tomorrowPrayerTimes.JamFajr2 || null;
          if (field === "dhuhr2") return tomorrowPrayerTimes.JamDhuhr2 || null;
          if (field === "asar2") return tomorrowPrayerTimes.JamAsar2 || null;
          if (field === "maghrib2")
            return tomorrowPrayerTimes.JamMaghrib2 || null;
          if (field === "isha2") return tomorrowPrayerTimes.JamEsha2 || null;

          // Beginning times
          if (field === "begFajr") return tomorrowPrayerTimes.BegFajr || null;
          if (field === "begDhuhr") return tomorrowPrayerTimes.BegDhuhr || null;
          if (field === "begAsar") return tomorrowPrayerTimes.BegAsar || null;
          if (field === "begMaghrib") {
            const beg = tomorrowPrayerTimes.BegMaghrib;
            return beg && beg !== "--:--"
              ? beg
              : tomorrowPrayerTimes.Sunset || null;
          }
          if (field === "begEsha") return tomorrowPrayerTimes.BegEsha || null;

          // Jummah times
          if (field === "jumuah1") return tomorrowPrayerTimes.Jummah1 || null;
          if (field === "jumuah2") return tomorrowPrayerTimes.Jummah2 || null;
          if (field === "jumuah3") return tomorrowPrayerTimes.Jummah3 || null;
          if (field === "jumuah4") return tomorrowPrayerTimes.Jummah4 || null;
          if (field === "jumuah5") return tomorrowPrayerTimes.Jummah5 || null;

          // Special times
          if (field === "sehriEnd") return tomorrowPrayerTimes.SehriEnd || null;
          if (field === "sunrise") return tomorrowPrayerTimes.Sunrise || null;
          if (field === "noon") return tomorrowPrayerTimes.Noon || null;

          return null;
        };

        // Get source time
        const todayTime = getSourceTime(sourceField);
        const tomorrowTime = getTomorrowSourceTime(sourceField);

        if (!todayTime) return "--:--";

        // Check if today's time has passed
        let useTime = todayTime;
        if (showTomorrowAfterPassed && tomorrowTime) {
          const now = currentTimeStr;
          if (now) {
            const [nowH, nowM] = now.split(":").map(Number);
            const [todayH, todayM] = todayTime.split(":").map(Number);
            const nowInMinutes = nowH * 60 + nowM;
            const todayInMinutes = todayH * 60 + todayM;

            if (nowInMinutes >= todayInMinutes) {
              useTime = tomorrowTime;
            }
          }
        }

        // Apply offset if in offset mode
        if (mode === "offset" && offsetMinutes !== 0) {
          const [hours, minutes] = useTime.split(":").map(Number);
          let totalMinutes = hours * 60 + minutes + offsetMinutes;

          // Handle day wraparound
          if (totalMinutes < 0) {
            totalMinutes += 24 * 60;
          } else if (totalMinutes >= 24 * 60) {
            totalMinutes -= 24 * 60;
          }

          const newHours = Math.floor(totalMinutes / 60);
          const newMinutes = totalMinutes % 60;
          useTime = `${newHours.toString().padStart(2, "0")}:${newMinutes.toString().padStart(2, "0")}`;
        }

        // Guard against unparseable times (e.g. "--:--")
        if (!useTime || useTime === "--:--") return "--:--";

        // Format for 12-hour display if needed
        if (screenData.use12HourFormat) {
          const [hours, minutes] = useTime.split(":").map(Number);
          if (isNaN(hours) || isNaN(minutes)) return "--:--";
          const hour12 = hours % 12 || 12;
          return `${hour12}:${minutes.toString().padStart(2, "0")}`;
        }

        return useTime;
      };

      const displayValue = getFlexibleTimeValue();

      return (
        <div
          key={id}
          style={{
            position: "absolute",
            left: `${positionX}px`,
            top: `${positionY}px`,
            width: `${width}px`,
            height: `${height}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: `${fontSize}px`,
            fontFamily,
            color,
            fontWeight,
          }}
        >
          {displayValue}
        </div>
      );
    }

    const position = component.Position || component.position;
    const style = component.Style || component.style || {};
    const size = component.Size || component.size;
    const clockStyle = component.ClockStyle || component.clockStyle;

    // Simple direct position extraction - no scaling or corrections
    let positionX = 0;
    let positionY = 0;

    if (position) {
      // Extract directly from position object
      positionX = position.X ?? position.x ?? 0;
      positionY = position.Y ?? position.y ?? 0;
    } else {
      // Fallback to component level properties if needed
      positionX = component.X ?? component.x ?? 0;
      positionY = component.Y ?? component.y ?? 0;
    }

    // NEW: Handle ticker components
    if (id?.startsWith("ticker")) {
      // Check if ticker is enabled in backend settings
      const isTickerEnabledInBackend = screenData.tickerEnabled ?? true;
      if (!isTickerEnabledInBackend) {
        return null; // Don't render ticker if disabled in backend
      }

      const tickerSettings =
        component.TickerSettings || component.tickerSettings;
      if (tickerSettings) {
        // Extract settings with fallbacks (styling from Designer)
        const mode = tickerSettings.Mode || tickerSettings.mode || "static";
        const staticText =
          tickerSettings.Text || tickerSettings.text || "Sample Ticker Text";
        const speed = tickerSettings.Speed || tickerSettings.speed || 50; // pixels per second
        const fontSize =
          tickerSettings.FontSize || tickerSettings.fontSize || 40;
        const fontFamily =
          tickerSettings.FontFamily || tickerSettings.fontFamily || "Arial";
        const color = tickerSettings.Color || tickerSettings.color || "#ffffff";
        const backgroundColor =
          tickerSettings.BackgroundColor ||
          tickerSettings.backgroundColor ||
          "transparent";
        const direction =
          tickerSettings.Direction || tickerSettings.direction || "left";
        const pauseOnHover =
          tickerSettings.PauseOnHover || tickerSettings.pauseOnHover || false;

        // Determine ticker text based on mode
        let tickerText = staticText;
        if (mode === "dynamic") {
          // Parse backend's ticker categories
          const tickerCategories: string[] = screenData.tickerCategories
            ? JSON.parse(screenData.tickerCategories)
            : ["Event"];

          const now = new Date();
          const MAX_TICKER_ITEM_LENGTH = 120;

          const truncateTickerItem = (value: string): string => {
            const normalized = value.replace(/\s+/g, " ").trim();
            if (normalized.length <= MAX_TICKER_ITEM_LENGTH) {
              return normalized;
            }
            return `${normalized.slice(0, MAX_TICKER_ITEM_LENGTH - 1).trimEnd()}…`;
          };

          const formatDisplayTime = (date: Date): string => {
            const hours = date.getHours();
            const minutes = date.getMinutes();

            if (screenData.use12HourFormat ?? true) {
              const ampm = hours >= 12 ? "PM" : "AM";
              const displayHour = hours % 12 || 12;
              return `${displayHour}:${minutes.toString().padStart(2, "0")} ${ampm}`;
            }

            return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
          };

          const formatRelativeTime = (target: Date): string => {
            const diffMs = target.getTime() - now.getTime();
            if (diffMs <= 0) {
              return "now";
            }

            const totalMinutes = Math.round(diffMs / 60000);
            if (totalMinutes < 60) {
              return `in ${totalMinutes}m`;
            }

            const hours = Math.floor(totalMinutes / 60);
            const mins = totalMinutes % 60;
            if (hours < 24) {
              return mins > 0 ? `in ${hours}h ${mins}m` : `in ${hours}h`;
            }

            const days = Math.floor(hours / 24);
            return days === 1 ? "in 1 day" : `in ${days} days`;
          };

          const formatReadableDate = (
            rawDate: string | Date | null | undefined,
            options?: { includeWeekday?: boolean; includeYear?: boolean },
          ): string | null => {
            if (!rawDate) {
              return null;
            }

            const parsed =
              rawDate instanceof Date ? rawDate : new Date(rawDate);
            if (Number.isNaN(parsed.getTime())) {
              return null;
            }

            const day = parsed.getDate();
            const remainder10 = day % 10;
            const remainder100 = day % 100;
            const suffix =
              remainder10 === 1 && remainder100 !== 11
                ? "st"
                : remainder10 === 2 && remainder100 !== 12
                  ? "nd"
                  : remainder10 === 3 && remainder100 !== 13
                    ? "rd"
                    : "th";

            const month = parsed.toLocaleString("en-GB", { month: "long" });
            const year = parsed.getFullYear();
            const includeYear = options?.includeYear ?? true;
            const baseDate = includeYear
              ? `${day}${suffix} ${month} ${year}`
              : `${day}${suffix} ${month}`;

            if (options?.includeWeekday) {
              const weekday = parsed.toLocaleString("en-GB", {
                weekday: "short",
              });
              return `${weekday} ${baseDate}`;
            }

            return baseDate;
          };

          const normalizeJanazahTime = (
            rawTime: string | null | undefined,
          ): string | null => {
            if (!rawTime) {
              return null;
            }

            const normalized = rawTime
              .trim()
              .replace(/\s+/g, " ")
              .replace(/\s*(AM|PM)$/i, "$1")
              .toLowerCase();

            return normalized.length > 0 ? normalized : null;
          };

          const getDayLabel = (date: Date): string => {
            const eventDay = new Date(date);
            eventDay.setHours(0, 0, 0, 0);

            const today = new Date(now);
            today.setHours(0, 0, 0, 0);

            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);

            if (eventDay.getTime() === today.getTime()) {
              return "Today";
            }
            if (eventDay.getTime() === tomorrow.getTime()) {
              return "Tomorrow";
            }

            return date.toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
            });
          };

          const isWithinActiveWindow = (item: any): boolean => {
            const activeFrom = item.assetActiveFromUtc
              ? new Date(item.assetActiveFromUtc)
              : null;
            const expiry = item.assetExpiryDate
              ? new Date(item.assetExpiryDate)
              : item.eventExpiryDate
                ? new Date(item.eventExpiryDate)
                : null;

            if (!expiry || expiry <= now) {
              return false;
            }

            if (activeFrom && activeFrom > now) {
              return false;
            }

            return true;
          };

          const buildJanazahMessage = (item: any): string => {
            const name =
              item.deceasedName ||
              item.eventTitle ||
              item.assetTitle ||
              "Funeral Notice";

            const fallbackMessage = truncateTickerItem(
              `${name} - See poster for further details.`,
            );

            const rawJanazahDate = item.janazahPrayerDate;
            const rawJanazahTime = item.janazahPrayerTime;

            const formattedJanazahDate = formatReadableDate(
              item.janazahPrayerDate,
              { includeWeekday: true, includeYear: false },
            );
            const formattedJanazahTime = normalizeJanazahTime(
              item.janazahPrayerTime,
            );

            const dateFormattingFailed =
              !!rawJanazahDate && !formattedJanazahDate;
            const timeFormattingFailed =
              !!rawJanazahTime && !formattedJanazahTime;
            if (dateFormattingFailed || timeFormattingFailed) {
              return fallbackMessage;
            }

            const details: string[] = [`Janazah Notice(s) ${name}`];
            const salahScheduleParts: string[] = [];
            if (formattedJanazahDate) {
              salahScheduleParts.push(formattedJanazahDate);
            }
            if (formattedJanazahTime) {
              salahScheduleParts.push(formattedJanazahTime);
            }

            if (item.janazahPrayerLocation) {
              salahScheduleParts.push(item.janazahPrayerLocation);
            }

            if (salahScheduleParts.length > 0) {
              details.push(`Janazah Salah : ${salahScheduleParts.join(" ")}`);
            }

            if (item.burialLocation) {
              details.push(`Burial Location: ${item.burialLocation}`);
            }

            if (details.length < 2) {
              return fallbackMessage;
            }

            return truncateTickerItem(details.join(" | "));
          };

          const buildInfoMessage = (item: any): string => {
            const title =
              item.eventTitle ||
              item.assetTitle ||
              item.asset?.title ||
              "Information";
            const description =
              item.assetDescription || item.eventAudience || "";

            if (!description) {
              return truncateTickerItem(title);
            }

            return truncateTickerItem(`${title} - ${description}`);
          };

          const mergeTickerLanes = (
            lanes: Record<"funeral" | "event" | "info", string[]>,
          ): string[] => {
            const pattern: Array<"event" | "info"> = ["event", "event", "info"];

            const merged: string[] = [...lanes.funeral];
            const queues = {
              event: [...lanes.event],
              info: [...lanes.info],
            };

            while (queues.event.length || queues.info.length) {
              let pushed = false;

              for (const lane of pattern) {
                if (queues[lane].length > 0) {
                  const next = queues[lane].shift();
                  if (next) {
                    merged.push(next);
                    pushed = true;
                  }
                }
              }

              if (!pushed) {
                break;
              }
            }

            return merged;
          };

          const effectiveTickerPlaylistItems = (
            processedScreenData.playlists || []
          ).flatMap((playlist: any) => {
            const sortedItems = (playlist.items || [])
              .slice()
              .sort(
                (a: any, b: any) =>
                  (a.displayOrder || 0) - (b.displayOrder || 0),
              );

            if (playlist.isDailyRotation && sortedItems.length > 0) {
              return getRotatedItems(
                playlist,
                sortedItems,
                prayerTimes,
                "[CustomDatabaseLayoutDisplay/Ticker]",
              );
            }

            return sortedItems;
          });

          const eventLane: string[] = [];
          const funeralLane: string[] = [];
          const infoLane: string[] = [];

          // 1. Events (from ICS calendar) - if "Event" is in categories
          if (tickerCategories.includes("Event") && tickerEvents.length > 0) {
            const today = new Date(now);
            today.setHours(0, 0, 0, 0);

            const eventTickerText = (event: HydratedEvent) => {
              const dayLabel = getDayLabel(event.start);
              const speakers = event.speakers ? ` - ${event.speakers}` : "";
              const audience = event.audience ? ` (${event.audience})` : "";
              const when = event.timeLabel
                ? `(${event.timeLabel})`
                : `at ${formatDisplayTime(event.start)} (${formatRelativeTime(event.start)})`;
              return truncateTickerItem(
                `${dayLabel}: ${event.summary}${speakers}${audience} ${when}`,
              );
            };

            // Filter today's events (only upcoming ones)
            const todaysEvents = tickerEvents
              .filter((event) => {
                const eventDate = new Date(event.start);
                eventDate.setHours(0, 0, 0, 0);
                return (
                  eventDate.getTime() === today.getTime() && event.start > now
                );
              })
              .map(eventTickerText);

            // Check for tomorrow's events
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(0, 0, 0, 0);

            const tomorrowsEvents = tickerEvents
              .filter((event) => {
                const eventDate = new Date(event.start);
                eventDate.setHours(0, 0, 0, 0);
                return eventDate.getTime() === tomorrow.getTime();
              })
              .map(eventTickerText);

            if (todaysEvents.length > 0) {
              eventLane.push(...todaysEvents);
            }
            if (tomorrowsEvents.length > 0) {
              eventLane.push(...tomorrowsEvents);
            }
          }

          // 2. Funeral Notices (from playlists) - if "FuneralNotice" is in categories
          if (
            tickerCategories.includes("FuneralNotice") &&
            processedScreenData.showFuneralNotices !== false &&
            processedScreenData.playlists
          ) {
            const funeralItems = effectiveTickerPlaylistItems
              .filter((item: any) => {
                const category = item.eventCategory || "Event";
                return (
                  category === "FuneralNotice" && isWithinActiveWindow(item)
                );
              })
              .map((item: any) => buildJanazahMessage(item));

            if (funeralItems.length > 0) {
              funeralLane.push(...funeralItems);
            }
          }

          // 3. Information Posters (from playlists) - if "InformationPoster" is in categories
          if (
            tickerCategories.includes("InformationPoster") &&
            processedScreenData.playlists
          ) {
            const infoItems = effectiveTickerPlaylistItems
              .filter((item: any) => {
                const category = item.eventCategory || "Event";
                return (
                  category === "InformationPoster" && isWithinActiveWindow(item)
                );
              })
              .map((item: any) => buildInfoMessage(item));

            if (infoItems.length > 0) {
              infoLane.push(...infoItems);
            }
          }

          const mergedTickerItems = mergeTickerLanes({
            funeral: funeralLane,
            event: eventLane,
            info: infoLane,
          });

          // Combine all parts
          if (mergedTickerItems.length > 0) {
            tickerText = mergedTickerItems.join("  •  ");
          } else {
            tickerText =
              staticText ||
              "Welcome - Latest updates and announcements will appear here";
          }
        }

        const containerWidth = size?.Width || size?.width || 400;
        const containerHeight = size?.Height || size?.height || 50;

        return (
          <div
            key={id}
            style={{
              position: "absolute",
              left: `${positionX}px`,
              top: `${positionY}px`,
            }}
          >
            <TickerElement
              text={tickerText}
              speed={speed}
              fontSize={fontSize}
              fontFamily={fontFamily}
              color={color}
              backgroundColor={backgroundColor}
              direction={direction as "left" | "right"}
              pauseOnHover={pauseOnHover}
              containerWidth={containerWidth}
              containerHeight={containerHeight}
            />
          </div>
        );
      }
    }

    const isClockElement = id?.includes("Clock");

    // Handle style properties (both cases) - no scaling
    const fontSize = style?.FontSize ?? style?.fontSize ?? 40;
    const fontFamily = style?.FontFamily ?? style?.fontFamily ?? "Arial";
    const color = style?.Color ?? style?.color ?? "#ffffff";
    // Get fontWeight with fallback to isBold for backwards compatibility
    const fontWeight =
      style?.FontWeight ??
      style?.fontWeight ??
      ((style?.IsBold ?? style?.isBold) ? 700 : 400);

    // Get text case for textTransform CSS
    const textCase = style?.TextCase ?? style?.textCase ?? "none";

    const elementStyle: React.CSSProperties = {
      position: "absolute",
      left: `${positionX}px`,
      top: `${positionY}px`,
      fontSize: `${fontSize}px`,
      fontFamily: fontFamily,
      color: color,
      fontWeight: fontWeight,
      textAlign: (style?.textAlign || "center") as any, // <-- Use saved value or default
      textTransform: textCase !== "none" ? (textCase as any) : undefined,
      userSelect: "none",
      whiteSpace: "nowrap",
      // Add debugging border to see if elements are rendering
      //fborder: process.env.NODE_ENV === "development" ? "0px solid red" : "none",
    };

    if (isClockElement && size) {
      // Handle size properties (both cases) - no scaling
      const width = size?.Width ?? size?.width ?? 100;
      const height = size?.Height ?? size?.height ?? 100;

      // Render analog clock
      return (
        <div
          key={id}
          style={{
            position: "absolute",
            left: `${positionX}px`,
            top: `${positionY}px`,
            width: `${width}px`,
            height: `${height}px`,
          }}
        >
          <AnalogClock
            size={Math.min(width, height)}
            isDarkMode={false}
            showSeconds={false}
            showFace={clockStyle?.ShowFace ?? clockStyle?.showFace ?? false}
            showBorder={
              clockStyle?.ShowBorder ?? clockStyle?.showBorder ?? false
            }
            showMarkings={
              clockStyle?.ShowMarkings ?? clockStyle?.showMarkings ?? false
            }
            showCenterPoint={
              clockStyle?.ShowCenterPoint ?? clockStyle?.showCenterPoint ?? true
            }
            hourHandColor={
              clockStyle?.HourHandColor ||
              clockStyle?.hourHandColor ||
              "#000000"
            }
            minuteHandColor={
              clockStyle?.MinuteHandColor ||
              clockStyle?.minuteHandColor ||
              "#000000"
            }
            hourHandThickness={
              clockStyle?.HourHandThickness ??
              clockStyle?.hourHandThickness ??
              6
            }
            minuteHandThickness={
              clockStyle?.MinuteHandThickness ??
              clockStyle?.minuteHandThickness ??
              4
            }
            hourHandLength={
              clockStyle?.HourHandLength ?? clockStyle?.hourHandLength
            }
            minuteHandLength={
              clockStyle?.MinuteHandLength ?? clockStyle?.minuteHandLength
            }
            centerPointSize={
              clockStyle?.CenterPointSize ?? clockStyle?.centerPointSize ?? 5
            }
            centerPointColor={
              clockStyle?.CenterPointColor ?? clockStyle?.centerPointColor
            }
            minuteMarkerSize={
              clockStyle?.MinuteMarkerSize ?? clockStyle?.minuteMarkerSize
            }
            minuteMarkerThickness={
              clockStyle?.MinuteMarkerThickness ??
              clockStyle?.minuteMarkerThickness ??
              2
            }
            minuteMarkerColor={
              clockStyle?.MinuteMarkerColor ?? clockStyle?.minuteMarkerColor
            }
            showMinuteMarkers={
              clockStyle?.ShowMinuteMarkers ?? clockStyle?.showMinuteMarkers
            }
            show5MinuteMarkers={
              clockStyle?.Show5MinuteMarkers ?? clockStyle?.show5MinuteMarkers
            }
            fiveMinuteMarkerSize={
              clockStyle?.FiveMinuteMarkerSize ??
              clockStyle?.fiveMinuteMarkerSize
            }
            fiveMinuteMarkerThickness={
              clockStyle?.FiveMinuteMarkerThickness ??
              clockStyle?.fiveMinuteMarkerThickness
            }
            fiveMinuteMarkerColor={
              clockStyle?.FiveMinuteMarkerColor ??
              clockStyle?.fiveMinuteMarkerColor
            }
            clockBackgroundColor={
              clockStyle?.ClockBackgroundColor ??
              clockStyle?.clockBackgroundColor
            }
            fixedTime={getClockTime(id)}
          />
        </div>
      );
    } else {
      // Render text element with countdown support
      const textStyle: React.CSSProperties = { ...elementStyle };
      if (size) {
        const width = size?.Width ?? size?.width;
        const height = size?.Height ?? size?.height;
        if (typeof width === "number") textStyle.width = `${width}px`;
        if (typeof height === "number") textStyle.height = `${height}px`;
      }

      // Apply flex centering and overflow control to ALL elements with defined size
      if (textStyle.width || textStyle.height) {
        textStyle.display = "flex";
        textStyle.alignItems = "center";
        textStyle.overflow = "hidden";
        const ta = (style?.TextAlign ?? style?.textAlign ?? "center") as string;
        textStyle.justifyContent =
          ta === "left" ? "flex-start" : ta === "right" ? "flex-end" : "center";
      }

      // Add special styling for Makrooh elements
      const isMakroohElement =
        id === "makroohSunrise" ||
        id === "makroohZawwal" ||
        id === "makroohSunset";

      // Check if this Makrooh element should be visible
      let shouldShowMakroohElement = false;
      let makroohFlashEnabled = false;
      if (isMakroohElement) {
        if (
          id === "makroohSunrise" &&
          isMakroohTime &&
          makroohReason === "sunrise"
        ) {
          shouldShowMakroohElement = true;
          // Check if flash is enabled (default to true if not specified)
          makroohFlashEnabled =
            component?.flash !== false && component?.Flash !== false;
        } else if (
          id === "makroohZawwal" &&
          isMakroohTime &&
          makroohReason === "zawwal"
        ) {
          shouldShowMakroohElement = true;
          makroohFlashEnabled =
            component?.flash !== false && component?.Flash !== false;
        } else if (
          id === "makroohSunset" &&
          isMakroohTime &&
          makroohReason === "sunset"
        ) {
          shouldShowMakroohElement = true;
          makroohFlashEnabled =
            component?.flash !== false && component?.Flash !== false;
        }

        // Hide Makrooh elements when not active
        if (!shouldShowMakroohElement) {
          textStyle.display = "none";
        } else {
          // Apply special Makrooh styling when active
          textStyle.backgroundColor =
            style?.BackgroundColor ??
            style?.backgroundColor ??
            "rgba(0,0,0,0.8)";
          textStyle.borderColor =
            style?.BorderColor ?? style?.borderColor ?? "#ff4444";
          textStyle.borderWidth = `${
            style?.BorderWidth ?? style?.borderWidth ?? 2
          }px`;
          textStyle.borderStyle = "solid";
          textStyle.padding = "10px";
          textStyle.borderRadius = "8px";
          textStyle.lineHeight = "normal";
          textStyle.whiteSpace = "normal"; // Allow text wrapping for warnings
          textStyle.wordWrap = "break-word";

          console.log(`🎨 Applied Makrooh styling to ${id}`, textStyle);
        }
      }

      // Apply styling for custom text elements
      if (id?.startsWith("customText_")) {
        // Apply background color if set
        if (style?.backgroundColor || style?.BackgroundColor) {
          textStyle.backgroundColor =
            style?.backgroundColor ?? style?.BackgroundColor;
        }

        // Apply border if both borderWidth and borderColor are set
        const borderWidth = style?.borderWidth ?? style?.BorderWidth;
        const borderColor = style?.borderColor ?? style?.BorderColor;
        if (borderWidth && borderColor) {
          textStyle.border = `${borderWidth}px solid ${borderColor}`;
          textStyle.borderRadius = "8px";
          textStyle.padding = "10px";
          textStyle.lineHeight = "normal";
          textStyle.whiteSpace = "normal";
          textStyle.wordWrap = "break-word";
        }

        // Always center vertically for custom text elements
        textStyle.display = "flex";
        textStyle.alignItems = "center";

        // Set horizontal alignment based on textAlign setting
        const textAlign = style?.textAlign || "center";
        if (textAlign === "left") {
          textStyle.justifyContent = "flex-start";
        } else if (textAlign === "right") {
          textStyle.justifyContent = "flex-end";
        } else {
          textStyle.justifyContent = "center";
        }
      }

      // Check if this is a conditional overlay element and apply its styling
      if (id === "conditionalOverlay1" && component) {
        const overlaySettings =
          component?.conditionalOverlay || component?.ConditionalOverlay;

        if (overlaySettings) {
          // Check if the overlay is currently active
          const isActive = isConditionalOverlayActive(overlaySettings);

          // Apply conditional overlay dimensions if specified (always apply dimensions)
          const overlayWidth = overlaySettings.Width || overlaySettings.width;
          const overlayHeight =
            overlaySettings.Height || overlaySettings.height;

          if (overlayWidth) {
            textStyle.width = `${overlayWidth}px`;
          }
          if (overlayHeight) {
            textStyle.height = `${overlayHeight}px`;
          }

          // Only apply background and font styling when ACTIVE
          if (isActive) {
            // Apply conditional overlay background color
            const overlayBgColor =
              overlaySettings.BackgroundColor ||
              overlaySettings.backgroundColor ||
              "rgba(0, 0, 0, 0.8)"; // Default dark background

            textStyle.backgroundColor = overlayBgColor;

            // Apply font styling
            const fontSize =
              overlaySettings.FontSize || overlaySettings.fontSize || 24;
            const fontFamily =
              overlaySettings.FontFamily ||
              overlaySettings.fontFamily ||
              "Arial";
            const fontColor =
              overlaySettings.FontColor ||
              overlaySettings.fontColor ||
              "#ffffff";
            const isBold =
              overlaySettings.IsBold ?? overlaySettings.isBold ?? true;
            const isItalic =
              overlaySettings.IsItalic ?? overlaySettings.isItalic ?? false;
            const isUnderline =
              overlaySettings.IsUnderline ??
              overlaySettings.isUnderline ??
              false;
            const textAlign =
              overlaySettings.TextAlign ||
              overlaySettings.textAlign ||
              "center";

            textStyle.fontSize = `${fontSize}px`;
            textStyle.fontFamily = fontFamily;
            textStyle.color = fontColor;
            textStyle.fontWeight = isBold ? "bold" : "normal";
            textStyle.fontStyle = isItalic ? "italic" : "normal";
            textStyle.textDecoration = isUnderline ? "underline" : "none";
            textStyle.textAlign = textAlign as any;

            // Add some default styling for better appearance
            textStyle.borderRadius = "8px";
            textStyle.padding = "10px";
            textStyle.display = "flex";
            textStyle.alignItems = "center";
            textStyle.justifyContent =
              textAlign === "left"
                ? "flex-start"
                : textAlign === "right"
                  ? "flex-end"
                  : "center";
            textStyle.lineHeight = "normal";
            textStyle.whiteSpace = "normal";
            textStyle.wordWrap = "break-word";
          } else {
            // When NOT active, make the overlay invisible (no background, no content)
            textStyle.backgroundColor = "transparent";
            textStyle.display = "none"; // Hide completely when not active
          }
        }
      }

      // Check if this is a Jamat prayer time that should be highlighted
      const checkJamatHighlight = () => {
        // Debug: Log available Jamat times

        // Helper to check if Jamat time is currently active
        const isJamatActive = (
          jamaatTime: string | undefined,
          salahLength: number | undefined,
        ): boolean => {
          if (!jamaatTime || !salahLength) return false;

          try {
            const now = getCurrentTimeInTimezone(screenData.timeZone);
            const [hours, minutes] = jamaatTime.split(":").map(Number);

            // Create today's Jamat time
            const jamaatDateTime = new Date();
            jamaatDateTime.setHours(hours, minutes, 0, 0);

            // Calculate the end time (Jamat time + duration in seconds)
            const endTime = new Date(
              jamaatDateTime.getTime() + salahLength * 1000,
            );

            return now >= jamaatDateTime && now <= endTime;
          } catch (error) {
            return false;
          }
        };

        // Get current day of week (0 = Sunday, 5 = Friday)
        const now = getCurrentTimeInTimezone(screenData.timeZone);
        const isFriday = now.getDay() === 5;

        // Check if this is a Jummah element (jumuah1/2/3/4)
        const isJumuahElement = id.startsWith("jumuah");

        // Jummah elements should only be highlighted on Friday
        if (isJumuahElement && !isFriday) {
          return false;
        }

        // Dhuhr should not be highlighted on Friday (Jummah takes precedence)
        if (id === "dhuhr" && isFriday) {
          return false;
        }

        // Map element IDs to their corresponding Jamat times and durations
        const jamaatMappings: {
          [key: string]: {
            time: string | undefined;
            length: number | undefined;
          };
        } = {
          fajr: { time: prayerTimes.JamFajr, length: screenData.fajrLength },
          dhuhr: { time: prayerTimes.JamDhuhr, length: screenData.dhuhrLength },
          asar: { time: prayerTimes.JamAsar, length: screenData.asarLength },
          maghrib: {
            time: prayerTimes.JamMaghrib,
            length: screenData.maghribLength,
          },
          isha: { time: prayerTimes.JamEsha, length: screenData.ishaLength },
          // Jummah times - only checked on Fridays (handled by early return above)
          jumuah1: {
            time: prayerTimes.Jummah1,
            length: screenData.jummahLength,
          },
          jumuah2: {
            time: prayerTimes.Jummah2,
            length: screenData.jummahLength,
          },
          jumuah3: {
            time: prayerTimes.Jummah3,
            length: screenData.jummahLength,
          },
          jumuah4: {
            time: prayerTimes.Jummah4,
            length: screenData.jummahLength,
          },
        };

        const mapping = jamaatMappings[id];
        if (mapping && isJamatActive(mapping.time, mapping.length)) {
          const currentSalahHighlightColor =
            screenData.currentSalahHighlightColor || "#FF0000";
          textStyle.color = currentSalahHighlightColor;
          textStyle.fontWeight = "bold";

          return true;
        }
        return false;
      };

      const isJamatHighlighted = checkJamatHighlight();

      // Apply next prayer highlighting if not already Jamat-highlighted
      if (!isJamatHighlighted) {
        const highlightSettings =
          component?.nextPrayerHighlight || component?.NextPrayerHighlight;
        const highlightEnabled =
          highlightSettings?.enabled ?? highlightSettings?.Enabled ?? false;

        if (highlightEnabled) {
          // Map element IDs to prayer indices (0=Fajr, 1=Dhuhr, 2=Asar, 3=Maghrib, 4=Isha)
          const nextPrayerIdMap: { [key: string]: number } = {
            fajr: 0,
            begFajr: 0,
            dhuhr: 1,
            begDhuhr: 1,
            asar: 2,
            begAsar: 2,
            maghrib: 3,
            begMaghrib: 3,
            isha: 4,
            begEsha: 4,
          };

          // On Friday, also map jumuah elements to index 1 (Dhuhr slot)
          const now = getCurrentTimeInTimezone(screenData.timeZone);
          const isFridayNow = now.getDay() === 5;
          if (isFridayNow) {
            nextPrayerIdMap["jumuah1"] = 1;
            nextPrayerIdMap["jumuah2"] = 1;
            nextPrayerIdMap["jumuah3"] = 1;
            nextPrayerIdMap["jumuah4"] = 1;
          }

          // Labels and tags can follow a prayer's highlight via highlightPrayer (e.g. "fajr")
          const prayerId =
            nextPrayerIdMap[id] !== undefined ? id : component?.highlightPrayer;
          const prayerIndex = nextPrayerIdMap[prayerId];
          if (prayerIndex !== undefined && prayerIndex === nextPrayer) {
            // On Friday, skip dhuhr highlighting (jumuah takes over), unless the layout's
            // dhuhr card shows Jumu'ah itself (onFriday)
            const skipDhuhrOnFriday =
              isFridayNow &&
              prayerId === "dhuhr" &&
              !(highlightSettings?.onFriday ?? highlightSettings?.OnFriday);
            if (!skipDhuhrOnFriday) {
              // Apply highlight color
              const hlColor =
                highlightSettings?.color ?? highlightSettings?.Color;
              if (hlColor) {
                textStyle.color = hlColor;
              }

              // Apply highlight background
              const hlBg =
                highlightSettings?.backgroundColor ??
                highlightSettings?.BackgroundColor;
              if (hlBg) {
                textStyle.backgroundColor = hlBg;
                textStyle.borderRadius = "4px";
                textStyle.padding = "2px 6px";
              }

              // Apply glow effect
              const hlGlow =
                highlightSettings?.glowColor ?? highlightSettings?.GlowColor;
              if (hlGlow) {
                textStyle.boxShadow = `0 0 10px ${hlGlow}, 0 0 20px ${hlGlow}`;
              }

              // Apply scale
              const hlScale =
                highlightSettings?.scale ?? highlightSettings?.Scale;
              if (hlScale && hlScale !== 1) {
                textStyle.transform = `scale(${hlScale})`;
                textStyle.transformOrigin = "center center";
              }
            }
          }
        }
      }

      // Check if this is a prayer element that should show countdown
      const prayerCountdownMap: { [key: string]: string } = {
        fajr: screenData.customizedNames?.fajr || "Fajr",
        dhuhr: screenData.customizedNames?.dhuhr || "Dhuhr",
        asar: screenData.customizedNames?.asar || "Asar",
        maghrib: screenData.customizedNames?.maghrib || "Maghrib",
        isha: screenData.customizedNames?.isha || "Isha",
      };

      const shouldShowCountdown =
        isCountdownActive &&
        prayerCountdownMap[id] &&
        countdownPrayer === prayerCountdownMap[id];

      // Determine className for flash animation
      const flashPrayerMap: Record<string, string> = {
        JamFajr: "fajr",
        JamDhuhr: "dhuhr",
        JamAsar: "asar",
        JamEsha: "isha",
      };
      const shouldFlashSalahChange =
        flashingPrayer && flashPrayerMap[flashingPrayer] === id;

      const className =
        isMakroohElement && makroohFlashEnabled && shouldShowMakroohElement
          ? "makrooh-flash-animation"
          : shouldFlashSalahChange
            ? "salah-change-flash"
            : undefined;

      return (
        <div
          key={id}
          style={{
            ...textStyle,
            ...(shouldFlashSalahChange
              ? ({
                  "--flash-speed": `${screenData.flashSalahChangesSpeed ?? 1}s`,
                  ...(screenData.flashSalahChangesColor
                    ? { color: screenData.flashSalahChangesColor }
                    : {}),
                } as React.CSSProperties)
              : {}),
          }}
          className={className}
        >
          {shouldShowCountdown ? (
            <PrayerCountdown
              prayerName={prayerCountdownMap[id]}
              seconds={countdownSeconds}
              textColor={screenData.countdownTextColor || "#FF0000"}
            />
          ) : (
            getPrayerTimeValue(id, component)
          )}
        </div>
      );
    }
  };

  if (parseError) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#000",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          fontSize: "24px",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <div>Custom Layout Parse Error</div>
        <div style={{ fontSize: "16px", marginTop: "20px", color: "#ff6666" }}>
          {parseError}
        </div>
      </div>
    );
  }

  if (!layoutData) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#000",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "24px",
          fontFamily: "Arial, sans-serif",
        }}
      >
        Loading Custom Layout...
      </div>
    );
  }

  const containerStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    // Home's black-and-white look makes the bands beside a layout white
    backgroundColor: "var(--home-letterbox, #000)",
    overflow: "hidden",
    fontFamily: "Arial, sans-serif",
  };

  // Fixed-size design surface that we scale uniformly and center
  const designSurfaceStyle: React.CSSProperties = {
    position: "absolute",
    width: `${DESIGN_WIDTH}px`,
    height: `${DESIGN_HEIGHT}px`,
    left: `${offsetLeft}px`,
    top: `${offsetTop}px`,
    transform: `scale(${scale})`,
    transformOrigin: "top left",
    ...masjidColourVars(screenData.masjidColor),
    // Handle background image or color
    ...(() => {
      // Priority: screenData.backgroundUrl (from API) > backgroundColor from settings > svgBackground
      const backgroundUrl = processedScreenData.backgroundUrl;

      const backgroundColor =
        layoutData.Settings?.BackgroundColor ||
        layoutData.settings?.BackgroundColor ||
        layoutData.settings?.backgroundColor;

      const svgBackground =
        layoutData.Settings?.SvgBackground ||
        layoutData.Settings?.svgBackground ||
        layoutData.settings?.SvgBackground ||
        layoutData.settings?.svgBackground;

      if (backgroundUrl) {
        return {
          backgroundImage: `url("${backgroundUrl}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        };
      } else if (svgBackground) {
        // SVG background will be rendered as a separate element
        return {};
      } else if (backgroundColor) {
        return {
          backgroundColor: backgroundColor,
        };
      }

      return {};
    })(),
  };

  return (
    <div style={containerStyle}>
      {/* Slideshow background image (if configured) */}
      {processedScreenData.slideshowBackgroundUrl && (
        <img
          src={processedScreenData.slideshowBackgroundUrl}
          alt=""
          onError={(e) => (e.currentTarget.style.display = "none")}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "fill" as const,
            zIndex: 0,
            pointerEvents: "none" as const,
          }}
        />
      )}
      {/* Debug info (only in dev mode) */}
      {process.env.NODE_ENV === "development" && (
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            backgroundColor: "rgba(0,0,0,0.7)",
            color: "#fff",
            padding: "10px",
            borderRadius: "5px",
            fontSize: "12px",
            zIndex: 9999,
          }}
        >
          <div>Custom Layout Active (Design Surface Mode)</div>
          <div>
            Components:{" "}
            {(layoutData.Components || layoutData.components)?.length || 0}
          </div>
          <div>Screen: {screenName}</div>
          <div>Masjid ID: {masjidId}</div>
          <div>Scale: {scale.toFixed(4)}</div>
          <div>
            Offsets: L {Math.round(offsetLeft)} px, T {Math.round(offsetTop)} px
          </div>
        </div>
      )}

      {/* Scaled, centered design surface (dynamic resolution based on screen settings) */}
      <div style={designSurfaceStyle}>
        {/* SVG Background */}
        {(() => {
          const svgBackground =
            layoutData.Settings?.SvgBackground ||
            layoutData.Settings?.svgBackground ||
            layoutData.settings?.SvgBackground ||
            layoutData.settings?.svgBackground;

          if (svgBackground && svgBackground.markup) {
            let processedMarkup = svgBackground.markup;
            if (svgBackground.colors && svgBackground.originalColors) {
              // Parse the SVG to modify only elements with matching data-custom-fill
              const parser = new DOMParser();
              const svgDoc = parser.parseFromString(
                svgBackground.markup,
                "image/svg+xml",
              );

              Object.entries(svgBackground.colors).forEach(
                ([key, newColor]) => {
                  const originalColor = svgBackground.originalColors[key];
                  if (originalColor) {
                    // Find elements with data-custom-fill matching this key
                    const elements = svgDoc.querySelectorAll(
                      `[data-custom-fill="${key}"]`,
                    );
                    elements.forEach((el) => {
                      el.setAttribute("fill", newColor);
                    });
                  }
                },
              );

              // Serialize back to string
              processedMarkup = new XMLSerializer().serializeToString(svgDoc);
            }
            return (
              <div
                dangerouslySetInnerHTML={{ __html: processedMarkup }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  zIndex: -1,
                  pointerEvents: "none",
                }}
              />
            );
          }
          return null;
        })()}

        {/* Render all components from layout data at exact design pixels */}
        <HomePageGapCoordinatorProvider>
          {(layoutData.Components || layoutData.components)?.map(
            (component: any, index: number) => {
              // Handle both Id and id properties
              const componentId = component.Id || component.id;
              if (!component || !componentId) {
                console.warn(
                  `Skipping invalid component at index ${index}:`,
                  component,
                );
                return null;
              }

              return renderElement(component, index);
            },
          )}
        </HomePageGapCoordinatorProvider>
      </div>
    </div>
  );
}
